import { clubStorage } from '../../core/state/club-session';
import { marketApi } from '../../core/http/market.api';
import { clubsApi } from '../../core/http/clubs.api';
import { playerProfileApi } from '../../core/http/player-profile.api';
import { PlayerProfile } from '../../core/models/player-profile';
import { mapClubListing, combineMarketPlayers } from '../../core/utils/market-listings.utils';
import { getMarketOfferErrorMessage } from '../../core/utils/currency.utils';
import { MoneyInputDirective } from '../../shared/ui/money-input.directive';
import { Component, OnInit, OnDestroy, effect, untracked, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { GameService } from '../../core/state/game.service';
import { CompetitionService } from '../../core/http/competition.service';
import {
  Player,
  Position,
  BackendMarketPlayer,
  ActiveOfferItem,
  BackendClubTransfer,
  TransferOffer,
  BackendHistoricalOfferItem,
  BackendHistoricalOffersResponse,
  OfferRejectionReason,
  getUnavailabilityTypeLabel,
  getUnavailabilityReasonLabel,
  PortfolioItem,
  ReleaseClauseAttemptPayload,
  ReleaseClauseAttemptResponse,
} from '../../core/models/types';
import {
  CoachListing,
  CoachMarket,
  CoachContract,
  CoachOffer,
  CoachOfferDto,
  calculateCoachOfferScore,
  isCoachContractActive,
} from '../../core/models/coaches.types';
import { evaluatePlayerContractAcceptance } from '../../core/simulation/simulation';
import { IconComponent } from '../../shared/ui/icon/icon.component';
import { PlayerProfileComponent } from '../../shared/ui/player-profile/player-profile.component';

export type MarketMainTab = 'market' | 'coaches' | 'watchlist';

export interface MySalesPlayerGroup {
  playerInstanceId: string;
  playerDefinitionId?: string;
  player: Player | null;
  name: string;
  crestUrl: string | null;
  realClubName: string | null;
  position: Position | string;
  positionClass: string;
  currentMarketValue: number;
  askingPrice?: number;
  listedForTransfer: boolean;
  rounds: Array<{
    roundId: string;
    offers: BackendClubTransfer[];
  }>;
  totalOffersCount: number;
  pendingDecisionsCount: number;
  hasAcceptedOffer: boolean;
}

@Component({
  selector: 'app-market',
  standalone: true,
  imports: [MoneyInputDirective, CommonModule, FormsModule, IconComponent, PlayerProfileComponent],
  templateUrl: './market.component.html',
  styleUrl: './market.component.scss',
})
export class MarketComponent implements OnInit, OnDestroy {
  viewingMarketPlayer: { playerDefinitionId: string; name: string } | null = null;

  canViewPlayerProfile(player: Player): boolean {
    return !player.isAmateur && player.playerType !== 'AMATEUR' && !!player.playerDefinitionId;
  }

  openPlayerProfile(player: Player): void {
    if (this.canViewPlayerProfile(player)) this.viewingMarketPlayer = { playerDefinitionId: player.playerDefinitionId!, name: player.name };
  }

  openOfferPlayerProfile(offer: ActiveOfferItem): void {
    if (offer.playerDefinitionId && !offer.isAmateur) {
      this.viewingMarketPlayer = { playerDefinitionId: offer.playerDefinitionId, name: offer.displayName };
    }
  }

  openHistoricalPlayerProfile(offer: BackendHistoricalOfferItem): void {
    if (offer.playerDefinitionId) {
      this.viewingMarketPlayer = { playerDefinitionId: offer.playerDefinitionId, name: offer.playerName || 'Futbolista' };
    }
  }

  readonly game = inject(GameService);
  readonly competitionService = inject(CompetitionService);
  private readonly route = inject(ActivatedRoute);
  readonly router = inject(Router);

  // Main canonical sections (Mercado, Entrenadores, Cartera)
  activeTab: MarketMainTab = 'market';

  // Sub-tabs inside Mercado section
  activeMarketTab: 'market' | 'my_sales' | 'offers' = 'market';
  onlyFreeAgents = false;
  onlyClubPlayers = false;
  readonly clubTransfers = signal<BackendClubTransfer[]>([]);
  readonly clubTransferProfiles = signal<Record<string, PlayerProfile>>({});
  readonly clubOffersError = signal<string | null>(null);
  readonly clubOffersLoading = signal(false);
  private offersLoadVersion = 0;
  private offersClubId: string | null = null;

  // --- MIS VENTAS (OFERTAS RECIBIDAS) ---
  readonly receivedClubTransfers = signal<BackendClubTransfer[]>([]);
  readonly receivedTransfersLoading = signal<boolean>(false);
  readonly receivedTransfersError = signal<string | null>(null);
  readonly processingRoundId = signal<string | null>(null);
  readonly processingOfferId = signal<string | null>(null);
  readonly processingAction = signal<'accept' | 'reject' | null>(null);
  readonly syncFailedRounds = signal<Set<string>>(new Set());
  readonly roundSyncErrorMessage = signal<string | null>(null);
  readonly conflictErrorMessage = signal<string | null>(null);
  readonly decisionErrorMessage = signal<string | null>(null);
  readonly decisionSuccessMessage = signal<string | null>(null);
  currentTime = signal<number>(Date.now());
  private clockTimer: any = null;

  negotiatingPlayer: Player | null = null;
  readonly editingOfferEstimatedDecision = signal<string | null>(null);
  filterPos = 'ALL';

  // Filters inside Ofertas Activas section
  offersFilter: 'all' | 'submitted' = 'all';
  offerListFilter: 'all' | 'active' | 'history' = 'all';

  // Filters inside Historial section
  historyFilterStatus: 'ALL' | 'WON' | 'LOST' | 'CANCELLED' = 'ALL';
  historySearchTerm = '';
  readonly historicalPlayerProfiles = signal<Record<string, PlayerProfile>>({});
  private historyProfileLoadVersion = 0;

  offeredSalary = 1.0;
  contractYears = 1;
  offeredClause = 10.0;
  offeredTransferFee = 6.0;
  offeredSigningBonus = 0.5;
  editingOfferId: string | null = null;
  private editingClubOffer: BackendClubTransfer | null = null;
  isSubmittingOffer = signal<boolean>(false);
  isCancellingOffer = signal<boolean>(false);
  modalError = signal<string | null>(null);
  private readonly clubOfferOperationIds = new Map<string, string>();

  activeCompetitionGroupId = signal<string | null>(null);
  isLoadingFreeAgents = signal<boolean>(false);
  readonly clubListedPlayers = signal<Player[]>([]);
  readonly sellerClubNames = signal<Record<string, string>>({});
  readonly portfolioOwnerNames = signal<Record<string, string>>({});
  readonly portfolioOwnerError = signal<string | null>(null);
  private portfolioLoadVersion = 0;
  readonly loadedFreePlayers = signal<Player[]>([]);
  private playerLoadVersion = 0;
  loadError = signal<string | null>(null);

  // --- COACHES (ENTRENADORES) STATE ---
  coachesSubTab: 'market' | 'offers' = 'market';
  coachOfferListFilter: 'all' | 'active' | 'history' = 'all';
  coachHistoryFilter = 'ALL';
  selectedCoachForOffer = signal<CoachListing | null>(null);
  coachSigningBonus = 1_000_000;
  coachAnnualSalary = 2_000_000;
  coachReleaseClause = 10_000_000;
  coachOfferIdempotencyId = '';
  coachModalError = signal<string | null>(null);
  coachModalSuccess = signal<string | null>(null);
  showCoachOfferModal = signal<boolean>(false);
  showCoachCancelConfirmModal = signal<boolean>(false);
  cancellingCoachOffer = signal<CoachOffer | null>(null);

  // --- CLAUSULAZO (RELEASE CLAUSE ATTEMPTS) STATE ---
  selectedPortfolioItemForClause = signal<PortfolioItem | null>(null);
  showReleaseClauseModal = signal<boolean>(false);
  clauseTransferFeeM = 0; // Cláusula descubierta fija (en M€)
  clauseAnnualSalaryM = 2.0; // Salario ofrecido (en M€)
  clauseContractYears = 3; // Temporadas
  clauseNewReleaseClauseM = 25.0; // Nueva cláusula (en M€)
  isSubmittingClause = signal<boolean>(false);
  clauseModalError = signal<string | null>(null);

  private coachContextVersion = 0;
  private destroyed = false;
  private lastCoachRefresh = 0;

  // Cuenta atrás común a todas las pestañas del mercado
  dailyTimeLeft = signal<string>('');
  dailyClosesAtFormatted = signal<string>('');
  isDailyMarketClosed = signal<boolean>(false);
  private dailyTimerRef: any = null;
  private lastPlayerRefresh = 0;

  private refreshingPlayerOffers = false;
  private queuedOffersRefresh = false;
  private lastOffersRefresh = 0;
  private marketWasHidden = false;
  private lastLoadedSection = '';
  private readonly resumeMarket = () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      this.marketWasHidden = true;
      return;
    }
    if (!this.marketWasHidden) return;
    this.marketWasHidden = false;
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
    if (this.destroyed || this.refreshingPlayerOffers || Date.now() - this.lastOffersRefresh < 2000) return;
    void this.refreshPlayerOffers();
    void this.loadFreeAgents(true);
    if (this.activeTab === 'coaches') void this.loadCoachesData(true);
  };

  async refreshPlayerOffers(force = false): Promise<void> {
    if (this.destroyed) return;
    if (this.refreshingPlayerOffers) {
      if (force) this.queuedOffersRefresh = true;
      return;
    }
    if (!force && Date.now() - this.lastOffersRefresh < 2000) return;
    this.refreshingPlayerOffers = true;
    this.lastOffersRefresh = Date.now();
    try {
      const page = this.game.historicalOffersData()?.page ?? 0;
      await Promise.allSettled([this.loadActiveOffers(), this.loadHistoricalOffers(page), this.loadReceivedOffers(true)]);
    } finally {
      this.refreshingPlayerOffers = false;
      if (this.queuedOffersRefresh) {
        this.queuedOffersRefresh = false;
        await this.refreshPlayerOffers(true);
      }
    }
  }

  constructor() {
    effect(() => {
      const clubId = this.game.userClub()?.id;
      const context = this.competitionService.activeContext();
      const groupId = context.clubId === clubId ? context.clubId : null;
      untracked(() => {
        this.coachContextVersion++;
        this.closeCoachOfferModal();
        this.closeCoachCancelModal();
        this.activeCompetitionGroupId.set(groupId || null);
        if (clubId && groupId) this.game.coaches.selectContext(clubId, groupId);
        else this.game.coaches.clearState();
        if (this.activeTab === 'coaches' && clubId && groupId) void this.loadCoachesData(true);
      });
    });
  }

  async ngOnInit(): Promise<void> {
    document.addEventListener('visibilitychange', this.resumeMarket);
    // Cargar ofertas activas, historial y ofertas recibidas al entrar a la pantalla de mercado para mostrar los badges
    void this.refreshPlayerOffers();
    void this.loadPortfolio(false);
    this.startDailyCountdownTimer();
    this.clockTimer = setInterval(() => this.currentTime.set(Date.now()), 1000);
    // Load the shared deadline even when opening coaches or watchlist directly.
    const initialParams = this.route.snapshot.queryParams;
    if (initialParams['tab'] === 'coaches' || initialParams['tab'] === 'watchlist' ||
        initialParams['tab'] === 'offers' || initialParams['subtab'] === 'offers') {
      void this.loadFreeAgents(false);
    }

    this.route.queryParams.subscribe((params) => {
      const tab = params['tab'];
      if (tab === 'offers') {
        this.activeTab = 'market';
        this.activeMarketTab = 'offers';
      } else if (tab && ['market', 'coaches', 'watchlist'].includes(tab)) {
        this.activeTab = tab;
        if (tab === 'market' && ['market', 'my_sales', 'offers'].includes(params['subtab'])) {
          this.activeMarketTab = params['subtab'];
        }
      }
      this.refreshEnteredSection();
    });
  }

  ngOnDestroy(): void {
    document.removeEventListener('visibilitychange', this.resumeMarket);
    this.destroyed = true;
    this.playerLoadVersion++;
    this.coachContextVersion++;
    this.stopDailyCountdownTimer();
    if (this.clockTimer) {
      clearInterval(this.clockTimer);
      this.clockTimer = null;
    }
  }

  setTab(tab: MarketMainTab): void {
    this.activeTab = tab;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab, subtab: tab === 'market' ? this.activeMarketTab : null },
      queryParamsHandling: 'merge',
    });
    this.refreshEnteredSection();
  }

  private refreshEnteredSection(): void {
    const key = `${this.activeTab}:${this.activeMarketTab}`;
    if (key === this.lastLoadedSection || this.destroyed) return;
    this.lastLoadedSection = key;
    if (this.activeTab === 'market') {
      void this.refreshPlayerOffers();
      if (this.activeMarketTab === 'market') void this.loadFreeAgents(false);
    } else if (this.activeTab === 'coaches') {
      void this.loadCoachesData(true);
    } else if (this.activeTab === 'watchlist') {
      void this.loadPortfolio(true);
    }
  }

  setCoachesSubTab(subTab: 'market' | 'offers'): void {
    this.coachesSubTab = subTab;
    if (subTab === 'offers') {
      this.loadCoachesData(false);
    }
  }

  async syncTabData(forceRefresh: boolean = false): Promise<void> {
    if (this.activeTab === 'market' && this.activeMarketTab !== 'offers') {
      await this.loadFreeAgents(forceRefresh);
    } else if (this.activeTab === 'market' && this.activeMarketTab === 'offers') {
      await Promise.all([this.loadActiveOffers(), this.loadHistoricalOffers()]);
    }
  }

  isCurrentTabLoading(): boolean {
    if (this.activeTab === 'market' && this.activeMarketTab !== 'offers') return this.isLoadingFreeAgents();
    if (this.activeTab === 'market' && this.activeMarketTab === 'offers') return this.game.isActiveOffersLoading();
    return false;
  }

  async loadFreeAgents(forceRefresh: boolean = false): Promise<void> {
    const request = ++this.playerLoadVersion;
    this.isLoadingFreeAgents.set(true);
    this.loadError.set(null);
    this.clubListedPlayers.set([]);
    this.sellerClubNames.set({});
    this.loadedFreePlayers.set([]);
    try {
      const unitId = await this.competitionService.getSelectedClubId();
      if (this.destroyed || request !== this.playerLoadVersion) return;
      if (!unitId || unitId === 'league-global-01') {
        this.loadError.set('No se encontró una unidad de competición activa para consultar el mercado.');
        return;
      }
      this.activeCompetitionGroupId.set(unitId);
      const [free, clubs] = await Promise.allSettled([
        this.game.fetchFreeAgentsListing(unitId, forceRefresh),
        marketApi.getClubListings(unitId),
      ]);
      if (this.destroyed || request !== this.playerLoadVersion) return;
      const errors: string[] = [];
      if (free.status === 'fulfilled' && !this.game.marketError()) {
        this.loadedFreePlayers.set(free.value);
      } else {
        errors.push('No se pudieron cargar los agentes libres.');
      }
      if (clubs.status === 'fulfilled') {
        this.clubListedPlayers.set(clubs.value
          .filter(listing => listing.status === 'ACTIVE')
          .map(mapClubListing));
        try {
          const ids = [...new Set(this.clubListedPlayers().map(player => player.clubId!))];
          const summaries = await clubsApi.getSummaries(ids);
          if (this.destroyed || request !== this.playerLoadVersion) return;
          this.sellerClubNames.set(Object.fromEntries(summaries.map(club => [club.id, club.name])));
          if (ids.some(id => !this.sellerClubNames()[id])) {
            errors.push('No se pudieron obtener todos los nombres de los clubes propietarios.');
          }
        } catch {
          if (this.destroyed || request !== this.playerLoadVersion) return;
          errors.push('No se pudieron cargar los nombres de los clubes propietarios.');
        }
      } else {
        errors.push('No se pudieron cargar los transferibles de los clubes.');
      }
      this.loadError.set(errors.length ? errors.join(' ') : null);
    } catch (err: any) {
      if (request === this.playerLoadVersion && !this.destroyed) {
        this.loadError.set(err?.message || 'Error al consultar el mercado.');
      }
    } finally {
      if (request === this.playerLoadVersion && !this.destroyed) this.isLoadingFreeAgents.set(false);
    }
  }

  async loadActiveOffers(): Promise<void> {
    const clubId = this.game.userClub()?.id;
    const request = ++this.offersLoadVersion;
    if (this.offersClubId !== clubId) {
      this.clubTransfers.set([]);
      this.clubTransferProfiles.set({});
      this.offersClubId = clubId || null;
    }
    this.clubOffersError.set(null);
    this.clubOffersLoading.set(true);
    const current = () => !this.destroyed && request === this.offersLoadVersion && this.game.userClub()?.id === clubId;
    const results = await Promise.allSettled([
      this.game.fetchActiveOffers('active'),
      clubId ? marketApi.getClubTransfers(clubId) : Promise.resolve([]),
    ]);
    if (!current()) return;
    const transfers = results[1];
    if (transfers.status === 'fulfilled') {
      const active = (transfers.value as BackendClubTransfer[]).filter(offer => offer.buyerClubId === clubId && offer.type !== 'GAME' &&
        !(offer.status === 'RELEASING' && offer.releaseOutcome === 'REJECTED') &&
        !['COMPLETED', 'FAILED', 'REJECTED', 'CANCELLED', 'EXPIRED'].includes(offer.status));
      this.clubTransfers.set(active);
      if (active.some(offer => offer.type === 'RENEWAL') && clubId) {
        try {
          await this.game.fetchSquadForClub(clubId, false);
        } catch {
          if (current()) this.clubOffersError.set('No se pudieron cargar los jugadores de las renovaciones. Puedes reintentar la consulta.');
        }
        if (!current()) return;
      }
    } else {
      this.clubOffersError.set('No se pudieron actualizar las ofertas por jugadores de clubes. Puedes reintentar la consulta.');
    }
    const ids = [...new Set(this.rawOffersList().map(offer => offer.playerDefinitionId)
      .filter((id): id is string => !!id))];
    const profiles = await Promise.allSettled(ids.map(id => playerProfileApi.get(id)));
    if (!current()) return;
    const known = { ...this.clubTransferProfiles() };
    profiles.forEach((result, index) => { if (result.status === 'fulfilled') known[ids[index]] = result.value; });
    this.clubTransferProfiles.set(known);
    this.clubOffersLoading.set(false);
  }

  async loadReceivedOffers(force = false, rethrow = false): Promise<void> {
    const clubId = this.game.userClub()?.id;
    if (!clubId) return;

    this.receivedTransfersLoading.set(true);
    this.receivedTransfersError.set(null);

    try {
      const [allTransfers] = await Promise.all([
        marketApi.getClubTransfers(clubId),
        this.game.fetchSquadForClub(clubId, force),
      ]);
      // Se consideran recibidas las operaciones cuyo sellerClubId coincide con el club del usuario.
      // No se mezclan con las ofertas enviadas por el usuario.
      const received = (allTransfers || []).filter((offer) => offer.sellerClubId === clubId);
      this.receivedClubTransfers.set(received);

      const buyerIds = [...new Set(received
        .filter(offer => offer.type !== 'GAME')
        .map(offer => offer.buyerClubId)
        .filter((id): id is string => !!id))];
      if (buyerIds.length) {
        try {
          const buyers = await clubsApi.getSummaries(buyerIds);
          const buyersById = new Map(buyers.map(buyer => [buyer.id, buyer]));
          this.receivedClubTransfers.set(received.map(offer => {
            const buyer = offer.buyerClubId ? buyersById.get(offer.buyerClubId) : undefined;
            return buyer ? { ...offer, buyerClubName: buyer.name, buyerClub: buyer } : offer;
          }));
          if (buyerIds.some(id => !buyersById.has(id))) {
            this.receivedTransfersError.set('No se pudieron obtener todos los nombres de los clubes compradores. Puedes actualizar para reintentar.');
          }
        } catch {
          this.receivedTransfersError.set('No se pudieron cargar los nombres de los clubes compradores. Puedes actualizar para reintentar.');
        }
      }

      // Cargar perfiles de jugadores para ofertas que tengan playerDefinitionId
      const defIds = [
        ...new Set(
          received
            .map((o) => o.player?.playerDefinitionId)
            .filter((id): id is string => !!id)
        ),
      ];
      const known = { ...this.clubTransferProfiles() };
      const missing = defIds.filter((id) => !known[id]);
      if (missing.length > 0) {
        const results = await Promise.allSettled(missing.map((id) => playerProfileApi.get(id)));
        results.forEach((res, index) => {
          if (res.status === 'fulfilled') {
            known[missing[index]] = res.value;
          }
        });
        this.clubTransferProfiles.set(known);
      }
    } catch (err: any) {
      console.error('Error cargando ofertas recibidas del club:', err);
      this.receivedTransfersError.set(
        'No se pudieron actualizar las ofertas recibidas por tus jugadores. Puedes reintentar la consulta.'
      );
      if (rethrow) throw err;
    } finally {
      this.receivedTransfersLoading.set(false);
    }
  }

  isDecisionAvailable(offer: BackendClubTransfer): boolean {
    const myClubId = this.game.userClub()?.id;
    if (!myClubId || !offer) return false;
    const isSeller = offer.sellerClubId === myClubId;
    const isAwaitingSeller = offer.status === 'AWAITING_SELLER';
    const notReleaseClause = offer.type !== 'RELEASE_CLAUSE';
    const deadlineMs = offer.decisionDeadline ? Date.parse(offer.decisionDeadline) : 0;
    const notExpired = !isNaN(deadlineMs) && deadlineMs > this.currentTime();
    return isSeller && isAwaitingSeller && notReleaseClause && notExpired;
  }

  isDecisionButtonBlocked(offer: BackendClubTransfer): boolean {
    if (!this.isDecisionAvailable(offer)) return true;
    const roundKey = offer.roundId ? String(offer.roundId) : 'default';
    if (this.processingRoundId() === roundKey) return true;
    if (this.syncFailedRounds().has(roundKey)) return true;
    return false;
  }

  isDecisionExpired(deadlineStr?: string): boolean {
    if (!deadlineStr) return false;
    const ms = Date.parse(deadlineStr);
    return !isNaN(ms) && ms <= this.currentTime();
  }

  async handleSellerDecision(offer: BackendClubTransfer, accept: boolean): Promise<void> {
    if (this.isDecisionButtonBlocked(offer)) return;

    const roundKey = offer.roundId ? String(offer.roundId) : 'default';
    this.processingRoundId.set(roundKey);
    this.processingOfferId.set(offer.id);
    this.processingAction.set(accept ? 'accept' : 'reject');

    this.conflictErrorMessage.set(null);
    this.decisionErrorMessage.set(null);
    this.decisionSuccessMessage.set(null);

    try {
      // 4. Envío de decisiones y gestión de expectedVersion:
      // Conserva el campo version recibido y lo envía como expectedVersion sin modificar localmente.
      await marketApi.submitSellerDecision(offer.id, {
        expectedVersion: offer.version,
        accept,
      });

      // 5. Actualización tras una decisión correcta:
      // Se recarga la lista completa de ofertas porque una decisión puede modificar otras de la misma ronda.
      this.decisionSuccessMessage.set(
        accept
          ? 'Oferta seleccionada para ejecución al cierre.'
          : 'Oferta rechazada correctamente.'
      );
      setTimeout(() => this.decisionSuccessMessage.set(null), 5000);

      try {
        await this.loadReceivedOffers(true, true);
        this.syncFailedRounds.update((set) => {
          const next = new Set(set);
          next.delete(roundKey);
          return next;
        });
        this.roundSyncErrorMessage.set(null);
      } catch (reloadErr) {
        console.error('Error al recargar ofertas tras guardar decisión:', reloadErr);
        this.syncFailedRounds.update((set) => new Set(set).add(roundKey));
        this.roundSyncErrorMessage.set(
          'La decisión se ha guardado, pero no se ha podido actualizar la lista. Se bloquean las decisiones de la ronda afectada hasta completar una actualización correcta.'
        );
      }
    } catch (err: any) {
      console.error('Error al procesar decisión del vendedor:', err);
      if (err?.status === 409) {
        // 8. Tratamiento de HTTP 409:
        // 1. No mostrar la decisión como guardada ni modificar estado local.
        // 3. Mostrar texto exacto de la US:
        this.conflictErrorMessage.set(
          'No se ha podido guardar tu decisión. La oferta ha cambiado o ya no admite decisiones. Revisa su estado actualizado.'
        );
        // 2. Recargar ofertas del club.
        try {
          await this.loadReceivedOffers(true, true);
        } catch {
          this.syncFailedRounds.update((set) => new Set(set).add(roundKey));
          this.roundSyncErrorMessage.set(
            'No se ha podido actualizar la lista de ofertas tras el conflicto. Las decisiones de esta ronda permanecen bloqueadas hasta actualizar los datos.'
          );
        }
      } else {
        // 9. Tratamiento de otros errores al decidir:
        // - Se informa de que no se ha podido confirmar la decisión por un problema de conexión u otro error inesperado.
        const msg = err?.message || 'Error de conexión';
        this.decisionErrorMessage.set(
          `No se ha podido confirmar la decisión por un problema de conexión u otro error inesperado (${msg}). Comprobando si se llegó a procesar...`
        );
        // - Se intenta recargar la lista de ofertas para verificar si la decisión llegó a procesarse.
        try {
          await this.loadReceivedOffers(true, true);
          const refreshed = this.receivedClubTransfers().find((o) => o.id === offer.id);
          const expectedDecision = accept ? 'ACCEPTED' : 'REJECTED';
          if (refreshed && refreshed.clubDecision === expectedDecision) {
            // - Si la recarga confirma el cambio, se actualiza la interfaz.
            this.decisionErrorMessage.set(null);
            this.decisionSuccessMessage.set(
              accept
                ? 'Oferta seleccionada para ejecución al cierre.'
                : 'Oferta rechazada correctamente.'
            );
            setTimeout(() => this.decisionSuccessMessage.set(null), 5000);
          } else {
            // - Si la recarga confirma que sigue pendiente, se permite reintentar la acción.
            this.decisionErrorMessage.set(
              'No se ha podido confirmar la decisión por un problema de conexión u otro error inesperado. La oferta sigue pendiente; puedes reintentar la acción.'
            );
          }
        } catch {
          // - Si la recarga no es concluyente o falla la conexión, se mantiene el bloqueo de decisiones en esa ronda hasta poder sincronizar el estado real con el backend.
          this.syncFailedRounds.update((set) => new Set(set).add(roundKey));
          this.roundSyncErrorMessage.set(
            'No se ha podido sincronizar con el servidor tras el error. Las decisiones de esta ronda permanecen bloqueadas hasta completar una actualización correcta.'
          );
        }
      }
    } finally {
      this.processingRoundId.set(null);
      this.processingOfferId.set(null);
      this.processingAction.set(null);
    }
  }

  async retryRoundSync(roundKey: string | string[]): Promise<void> {
    this.receivedTransfersLoading.set(true);
    try {
      await this.loadReceivedOffers(true, true);
      this.syncFailedRounds.update((set) => {
        const next = new Set(set);
        for (const key of Array.isArray(roundKey) ? roundKey : [roundKey]) next.delete(key);
        return next;
      });
      this.roundSyncErrorMessage.set(null);
      this.conflictErrorMessage.set(null);
      this.decisionErrorMessage.set(null);
    } catch (err) {
      console.error('Fallo en reintento de sincronización:', err);
    } finally {
      this.receivedTransfersLoading.set(false);
    }
  }

  mySalesPlayerGroups(): MySalesPlayerGroup[] {
    const squadPlayers = this.game.userSquad();
    const listedSquadPlayers = squadPlayers.filter((p) => p.listedForTransfer);
    const receivedOffers = this.receivedClubTransfers();

    const groupsMap = new Map<string, MySalesPlayerGroup>();

    // 1. Añadir futbolistas de la plantilla actualmente en venta
    for (const player of listedSquadPlayers) {
      const key = player.playerInstanceId || player.id;
      groupsMap.set(key, {
        playerInstanceId: key,
        playerDefinitionId: player.playerDefinitionId,
        player,
        name: player.name,
        crestUrl: this.getPlayerCrestUrl(player),
        realClubName: this.getPlayerClubName(player),
        position: player.position,
        positionClass: this.getPositionBadge(player.position),
        currentMarketValue: player.currentMarketValue || 0,
        askingPrice: player.askingPrice,
        listedForTransfer: true,
        rounds: [],
        totalOffersCount: 0,
        pendingDecisionsCount: 0,
        hasAcceptedOffer: false,
      });
    }

    // 2. Asociar ofertas recibidas a cada jugador o crear grupo
    for (const offer of receivedOffers) {
      const instanceId = offer.playerInstanceId;
      const defId = offer.player?.playerDefinitionId;

      let existingGroup = groupsMap.get(instanceId);
      if (!existingGroup && defId) {
        for (const grp of groupsMap.values()) {
          if (grp.playerDefinitionId === defId) {
            existingGroup = grp;
            break;
          }
        }
      }

      if (!existingGroup) {
        const squadP = squadPlayers.find(
          (p) => (instanceId && (p.playerInstanceId === instanceId || p.id === instanceId)) ||
                 (defId && p.playerDefinitionId === defId)
        );
        const profile = defId ? this.clubTransferProfiles()[defId] : undefined;
        const name = squadP?.name || profile?.displayName || offer.player?.displayName || offer.player?.name || 'Futbolista';
        const position = squadP?.position || profile?.position || (offer.player?.position as Position) || 'DEL';
        const mv = squadP?.currentMarketValue ?? ((profile?.marketValue ?? offer.player?.currentMarketValue ?? 0) / 1_000_000);
        const realClub = profile?.realClub || offer.player?.realClub;
        const key = instanceId || defId || offer.id;

        existingGroup = {
          playerInstanceId: key,
          playerDefinitionId: defId || squadP?.playerDefinitionId,
          player: squadP || null,
          name,
          crestUrl: squadP ? this.getPlayerCrestUrl(squadP) : realClub?.shortName ? `crest/${realClub.shortName.toUpperCase()}.png` : null,
          realClubName: squadP ? this.getPlayerClubName(squadP) : realClub?.name || null,
          position,
          positionClass: this.getPositionBadge(position),
          currentMarketValue: mv,
          askingPrice: squadP?.askingPrice,
          listedForTransfer: squadP?.listedForTransfer ?? false,
          rounds: [],
          totalOffersCount: 0,
          pendingDecisionsCount: 0,
          hasAcceptedOffer: false,
        };
        groupsMap.set(key, existingGroup);
      }

      const roundKey = offer.roundId ? String(offer.roundId) : 'default';
      let roundObj = existingGroup.rounds.find((r) => r.roundId === roundKey);
      if (!roundObj) {
        roundObj = { roundId: roundKey, offers: [] };
        existingGroup.rounds.push(roundObj);
      }
      roundObj.offers.push(offer);
    }

    // Ordenar ofertas y calcular estadísticas
    const result: MySalesPlayerGroup[] = [];
    for (const group of groupsMap.values()) {
      group.rounds.sort((a, b) => b.roundId.localeCompare(a.roundId, undefined, { numeric: true }));

      let totalOffers = 0;
      let pendingCount = 0;
      let hasAccepted = false;

      for (const r of group.rounds) {
        r.offers.sort((a, b) => (b.terms?.transferAmount || 0) - (a.terms?.transferAmount || 0));
        totalOffers += r.offers.length;
        for (const o of r.offers) {
          if (o.status === 'AWAITING_SELLER' && this.isDecisionAvailable(o)) {
            pendingCount++;
          }
          if (o.clubDecision === 'ACCEPTED') {
            hasAccepted = true;
          }
        }
      }

      group.totalOffersCount = totalOffers;
      group.pendingDecisionsCount = pendingCount;
      group.hasAcceptedOffer = hasAccepted;

      result.push(group);
    }

    return result.sort((a, b) => {
      if (b.pendingDecisionsCount !== a.pendingDecisionsCount) {
        return b.pendingDecisionsCount - a.pendingDecisionsCount;
      }
      if (b.totalOffersCount !== a.totalOffersCount) {
        return b.totalOffersCount - a.totalOffersCount;
      }
      return (b.currentMarketValue || 0) - (a.currentMarketValue || 0);
    });
  }

  openSalesPlayerProfile(group: MySalesPlayerGroup): void {
    if (group.playerDefinitionId) {
      this.viewingMarketPlayer = {
        playerDefinitionId: group.playerDefinitionId,
        name: group.name,
      };
    } else if (group.player) {
      this.openPlayerProfile(group.player);
    }
  }

  formatTransferEuros(amount?: number | null): string {
    if (amount === null || amount === undefined) return '0 €';
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(amount);
  }

  getBuyerClubName(offer: BackendClubTransfer): string {
    if (offer.type === 'GAME' || !offer.buyerClubId) {
      return 'Oferta extranjera';
    }
    if (offer.buyerClubName) return offer.buyerClubName;
    if (offer.buyerClub?.name) return offer.buyerClub.name;
    return this.getClubName(offer.buyerClubId);
  }

  getBuyerClubCrest(offer: BackendClubTransfer): string | null {
    if (offer.type === 'GAME' || !offer.buyerClubId) {
      return null;
    }
    const club = offer.buyerClub || this.game.clubs().find((c) => c.id === offer.buyerClubId);
    return club?.code ? `/crest/${club.code}.png` : null;
  }

  formatDecisionDeadline(deadlineStr?: string): string {
    if (!deadlineStr) return 'Sin fecha límite';
    const d = new Date(deadlineStr);
    if (isNaN(d.getTime())) return deadlineStr;
    return d.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  getDeadlineRemainingBadge(deadlineStr?: string): { text: string; isExpired: boolean; isUrgent: boolean } {
    if (!deadlineStr) {
      return { text: 'Sin límite', isExpired: false, isUrgent: false };
    }
    const deadlineMs = Date.parse(deadlineStr);
    if (isNaN(deadlineMs)) {
      return { text: deadlineStr, isExpired: false, isUrgent: false };
    }
    const diff = deadlineMs - this.currentTime();
    if (diff <= 0) {
      return { text: 'Plazo expirado', isExpired: true, isUrgent: false };
    }

    const totalSeconds = Math.floor(diff / 1000);
    const totalMinutes = Math.floor(totalSeconds / 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const days = Math.floor(hours / 24);

    if (days > 1) {
      return { text: `Quedan ${days} días`, isExpired: false, isUrgent: false };
    }
    if (hours > 0) {
      return { text: `Quedan ${hours}h ${minutes}m`, isExpired: false, isUrgent: hours <= 2 };
    }
    return { text: `Quedan ${minutes}m`, isExpired: false, isUrgent: true };
  }

  getSellerDecisionLabel(offer: BackendClubTransfer): string {
    if (offer.clubDecision === 'ACCEPTED') {
      return 'Seleccionada para ejecución al cierre';
    }
    if (offer.clubDecision === 'REJECTED') {
      return 'Rechazada';
    }
    return 'Pendiente de decisión';
  }

  getOperationStatusLabel(status: string): string {
    const s = (status || '').toUpperCase();
    switch (s) {
      case 'COMPLETED':
        return 'Venta completada';
      case 'AWAITING_SELLER':
        return 'Pendiente de respuesta';
      case 'AWAITING_BUYER':
        return 'Pendiente del comprador';
      case 'AWAITING_PLAYER':
        return 'Pendiente del futbolista';
      case 'CANCELLED':
        return 'Cancelada';
      case 'EXPIRED':
        return 'Plazo vencido';
      case 'REJECTED':
        return 'Rechazada';
      case 'PROCESSING':
        return 'En tramitación';
      default:
        return status || 'En trámite';
    }
  }

  formatRoundTitle(roundId: string): string {
    if (!roundId || roundId === 'default' || roundId === 'Ronda sin especificar') {
      return 'Ronda de Mercado';
    }
    if (roundId.toLowerCase().startsWith('round-') || roundId.toLowerCase().startsWith('r-')) {
      const num = roundId.replace(/^[a-zA-Z-]+/, '');
      return `Ronda ${num}`;
    }
    return `Ronda ${roundId}`;
  }

  async loadHistoricalOffers(page = 0): Promise<void> {
    const request = ++this.historyProfileLoadVersion;
    try {
      await this.game.fetchHistoricalOffers(page, 50);
      if (this.destroyed || request !== this.historyProfileLoadVersion) return;
      const ids = [...new Set(this.historicalOffersList().map(item => item.playerDefinitionId)
        .filter((id): id is string => !!id && !this.historicalPlayerProfiles()[id]))];
      const profiles = await Promise.allSettled(ids.map(id => playerProfileApi.get(id)));
      if (this.destroyed || request !== this.historyProfileLoadVersion) return;
      this.historicalPlayerProfiles.update(known => {
        const next = { ...known };
        profiles.forEach((result, index) => {
          if (result.status === 'fulfilled') next[ids[index]] = result.value;
        });
        return next;
      });
    } catch (err) {
      console.error('Error fetching historical offers:', err);
    }
  }

  activePlayerAge(offer: ActiveOfferItem): number | null {
    const profile = offer.playerDefinitionId ? this.clubTransferProfiles()[offer.playerDefinitionId] : undefined;
    return profile?.age ?? this.game.allPlayers().find(p => (p.playerInstanceId || p.id) === offer.playerInstanceId)?.age ?? null;
  }

  activePlayerClub(offer: ActiveOfferItem): PlayerProfile['realClub'] {
    const profile = offer.playerDefinitionId ? this.clubTransferProfiles()[offer.playerDefinitionId] : undefined;
    return profile?.realClub ?? this.game.allPlayers().find(p => (p.playerInstanceId || p.id) === offer.playerInstanceId)?.realClub ?? null;
  }

  activePlayerCrest(offer: ActiveOfferItem): string | null {
    const code = this.activePlayerClub(offer)?.shortName;
    return code ? `assets/crest/${code.toUpperCase()}.png` : null;
  }

  historicalPlayerAge(item: BackendHistoricalOfferItem): number | null {
    return item.playerDefinitionId ? this.historicalPlayerProfiles()[item.playerDefinitionId]?.age ?? null : null;
  }

  historicalPlayerPosition(item: BackendHistoricalOfferItem): string | null {
    const position = item.playerDefinitionId ? this.historicalPlayerProfiles()[item.playerDefinitionId]?.position : null;
    return position ? this.normalizePos(position) : null;
  }

  historicalPlayerClub(item: BackendHistoricalOfferItem): PlayerProfile['realClub'] {
    return item.playerDefinitionId ? this.historicalPlayerProfiles()[item.playerDefinitionId]?.realClub ?? null : null;
  }

  historicalPlayerCrest(item: BackendHistoricalOfferItem): string | null {
    const code = this.historicalPlayerClub(item)?.shortName;
    return code ? `assets/crest/${code.toUpperCase()}.png` : null;
  }

  historicalOffersList(): BackendHistoricalOfferItem[] {
    const data = this.game.historicalOffersData();
    if (!data || !data.content) return [];
    return data.content;
  }

  historyOfferTypeLabel(item: BackendHistoricalOfferItem): string {
    if (item.offerType === 'RELEASE_CLAUSE') return 'Cláusula de rescisión';
    if (item.offerType && item.offerType !== 'FREE_AGENT') return 'Jugador con contrato';
    return 'Agente libre';
  }

  toggleOfferListFilter(filter: 'active' | 'history'): void {
    if (this.offerListFilter === 'all') {
      this.offerListFilter = filter === 'active' ? 'history' : 'active';
    } else if (this.offerListFilter !== filter) {
      this.offerListFilter = 'all';
    }
    this.historyFilterStatus = 'ALL';
  }

  setHistoryStatusFilter(status: string): void {
    if (status === 'ALL' || status === 'WON' || status === 'LOST' || status === 'CANCELLED') {
      this.offerListFilter = 'history';
      this.historyFilterStatus = this.historyFilterStatus === status ? 'ALL' : status;
    }
  }

  filteredHistoricalOffers(): BackendHistoricalOfferItem[] {
    let list = this.historicalOffersList();
    if (this.historyFilterStatus !== 'ALL') {
      list = list.filter((item) => (item.status || '').toUpperCase() === this.historyFilterStatus);
    }
    if (this.historySearchTerm.trim()) {
      const term = this.historySearchTerm.toLowerCase().trim();
      list = list.filter((item) => (item.playerName || '').toLowerCase().includes(term));
    }
    return list;
  }

  historicalCountWon(): number {
    return this.historicalOffersList().filter((i) => (i.status || '').toUpperCase() === 'WON').length;
  }

  historicalCountLost(): number {
    return this.historicalOffersList().filter((i) => (i.status || '').toUpperCase() === 'LOST').length;
  }

  historicalCountCancelled(): number {
    return this.historicalOffersList().filter((i) => (i.status || '').toUpperCase() === 'CANCELLED').length;
  }

  historicalTotalInvestedWonM(): number {
    return this.historicalOffersList()
      .filter((i) => (i.status || '').toUpperCase() === 'WON')
      .reduce((acc, curr) => acc + (Number(curr.terms?.transferAmount || 0) / 1_000_000), 0);
  }

  formatHistoryDate(isoDate?: string): string {
    if (!isoDate) return '-';
    try {
      const d = new Date(isoDate);
      if (isNaN(d.getTime())) return isoDate;
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      const hours = String(d.getHours()).padStart(2, '0');
      const mins = String(d.getMinutes()).padStart(2, '0');
      return `${day}/${month}/${year} ${hours}:${mins}`;
    } catch {
      return isoDate;
    }
  }

  getHistoryStatusInfo(status?: string, reason?: string | null): { label: string; class: string; icon: string } {
    const s = (status || '').toUpperCase();
    if (s === 'WON') {
      return {
        label: this.game.t('market.history_won_badge'),
        class: 'bg-emerald-50 text-emerald-700 border-emerald-300 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-700',
        icon: 'trophy',
      };
    }
    if (s === 'LOST') {
      const rejection = (reason || '').trim().toUpperCase();
      const rejected = ['RELEASE_CLAUSE_TOO_HIGH', 'ECONOMIC_OFFER_TOO_LOW',
        'PLAYER_REJECTED', 'SELLER_REJECTED', 'BOARD_RELEASE_CLAUSE_EXCEEDED', 'BOARD_MARKET_VALUE_EXCEEDED'].includes(rejection);
      return {
        label: rejection === 'BELOW_GAME_OFFER' ? 'Anulada' : rejected ? 'Rechazada' : this.game.t('market.history_lost_badge'),
        class: rejected
          ? 'bg-amber-50 text-amber-700 border-amber-300 dark:bg-amber-950/60 dark:text-amber-300 dark:border-amber-700'
          : 'bg-rose-50 text-rose-700 border-rose-300 dark:bg-rose-950/60 dark:text-rose-300 dark:border-rose-700',
        icon: 'x-circle',
      };
    }
    if (s === 'CANCELLED') {
      return {
        label: this.game.t('market.history_cancelled_badge'),
        class: 'bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700',
        icon: 'ban',
      };
    }
    if (s === 'FAILED') {
      return { label: 'Fallida', class: 'bg-rose-50 text-rose-700 border-rose-300 dark:bg-rose-950/60 dark:text-rose-300 dark:border-rose-700', icon: 'alert-circle' };
    }
    return {
      label: s,
      class: 'bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700',
      icon: 'clock',
    };
  }

  getRejectionReasonLabel(reason?: OfferRejectionReason | null, boardLimit?: number | null): string {
    if (!reason) return '';
    const r = reason.trim().toUpperCase();
    const key = `market.rejection_reason_${r}`;
    const translated = this.game.t(key);
    if (translated && translated !== key) {
      return translated;
    }
    const limit = boardLimit == null ? '' : ` (${new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(boardLimit)} €)`;
    switch (r) {
      case 'BOARD_RELEASE_CLAUSE_EXCEEDED': return `La directiva rechazó la oferta: supera la cl€usula de rescisión del jugador${limit}`;
      case 'BOARD_MARKET_VALUE_EXCEEDED': return `La directiva rechazó la oferta: supera el doble del valor de mercado. M€ximo permitido${limit}`;
      case 'BELOW_GAME_OFFER': return 'Importe inferior al mínimo de la liga';
      case 'FUNDS_NOT_READY_AT_CLOSE': return 'Fondos no disponibles al cierre';
      case 'PLAYER_REJECTED': return 'El jugador rechazó la oferta';
      case 'SELLER_REJECTED': return 'El club vendedor rechazó la oferta';
      case 'EXPIRED': return 'La oferta caducó sin completarse';
      case 'TRANSFER_FAILED': return 'No se pudo completar el traspaso';
      case 'ACCEPTED_OTHER_CLUB':
        return 'Aceptó la oferta de otro club';
      case 'RELEASE_CLAUSE_TOO_HIGH':
        return 'Cláusula de rescisión demasiado alta';
      case 'ECONOMIC_OFFER_TOO_LOW':
        return 'Oferta económica insuficiente';
      default:
        return reason
          .toLowerCase()
          .replace(/_/g, ' ')
          .replace(/^\w/, (c) => c.toUpperCase());
    }
  }

  getRejectionReasonIcon(reason?: OfferRejectionReason | null): string {
    if (!reason) return 'alert-circle';
    const r = reason.trim().toUpperCase();
    switch (r) {
      case 'ACCEPTED_OTHER_CLUB':
        return 'building';
      case 'RELEASE_CLAUSE_TOO_HIGH':
        return 'alert-circle';
      case 'ECONOMIC_OFFER_TOO_LOW':
        return 'coins';
      default:
        return 'alert-circle';
    }
  }

  selectMarketTab(tab: 'market' | 'my_sales' | 'offers'): void {
    this.activeMarketTab = tab;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: 'market', subtab: tab },
      queryParamsHandling: 'merge',
    });
    this.refreshEnteredSection();
  }

  marketCount(): number {
    return this.marketPlayers().length;
  }

  mySalesCount(): number {
    return this.game.userSquad().filter((p) => p.listedForTransfer).length;
  }

  mySalesPendingOffersCount(): number {
    return this.receivedClubTransfers().filter(
      (o) => o.status === 'AWAITING_SELLER' && this.isDecisionAvailable(o)
    ).length;
  }

  activeOffersCount(): number {
    return this.rawOffersList().length;
  }

  watchlistCount(): number {
    return this.game.portfolioCount();
  }

  formatAvg(val?: number | null): string {
    if (val === null || val === undefined) return '—';
    const num = Number(val);
    if (isNaN(num)) return '—';
    return parseFloat(num.toFixed(2)).toString();
  }

  hasCustomAverages(player?: Player | null): boolean {
    if (!player) return false;
    // En la pestaña de mercado de agentes libres (/market) o para cualquier agente libre de /current-listing
    if (
      this.activeTab === 'market' ||
      this.activeMarketTab === 'market' ||
      !player.clubId ||
      !!player.playerInstanceId ||
      'averagePoints' in player ||
      'last3AveragePoints' in player
    ) {
      return true;
    }
    return (
      player.averagePoints !== undefined ||
      player.last3AveragePoints !== undefined
    );
  }

  marketPlayers(): Player[] {
    return combineMarketPlayers(this.loadedFreePlayers(), this.clubListedPlayers());
  }

  currentMarketList(): Player[] {
    let source: Player[] = [];
    if (this.activeMarketTab === 'market') {
      source = this.isLoadingFreeAgents() ? [] : this.marketPlayers();
      if (this.onlyFreeAgents) source = source.filter(player => player.clubId === null);
      if (this.onlyClubPlayers) source = source.filter(player => !!player.clubId);
    } else {
      source = this.game.userSquad().filter((p) => p.listedForTransfer);
    }

    return source
      .filter((p) => {
        const matchPos = this.filterPos === 'ALL' || p.position === this.filterPos;
        return matchPos;
      })
      .sort((a, b) => {
        const order: Record<Position, number> = { POR: 0, DEF: 1, MED: 2, DEL: 3 };
        const positionOrder = order[a.position] - order[b.position];
        if (positionOrder !== 0) return positionOrder;
        const valB = b.averagePoints ?? b.overall ?? 0;
        const valA = a.averagePoints ?? a.overall ?? 0;
        return valB - valA;
      });
  }

  watchlistPlayers(): Player[] {
    const ids = this.game.watchlistPlayerIds();
    if (!ids || ids.length === 0) return [];

    const all = [...this.marketPlayers(), ...this.game.opponentStars(), ...this.game.userSquad()];
    const seen = new Set<string>();
    const result: Player[] = [];

    for (const p of all) {
      if ((ids.includes(p.id) || (p.playerInstanceId && ids.includes(p.playerInstanceId))) && !seen.has(p.id)) {
        seen.add(p.id);
        result.push(p);
      }
    }
    return result;
  }

  rawOffersList(): ActiveOfferItem[] {
    const rawOffers = this.game.activeOffersData();
    const localOffers = this.game.transferOffers();
    const items: ActiveOfferItem[] = [];
    const seenPlayerInstanceIds = new Set<string>();
    const seenOfferIds = new Set<string>();

    let list: any[] = [];
    if (Array.isArray(rawOffers)) {
      list = rawOffers;
    } else if (rawOffers && typeof rawOffers === 'object') {
      if (Array.isArray((rawOffers as any).offers)) {
        list = (rawOffers as any).offers;
      } else if (Array.isArray((rawOffers as any).players)) {
        list = (rawOffers as any).players;
      }
    }

    for (const off of list) {
      const p = off.player || (off.displayName ? off : {}) || {};
      const terms = off.terms || {
        transferAmount: off.transferAmount ?? p.marketValue ?? 0,
        annualSalary: off.annualSalary ?? 0,
        releaseClause: off.releaseClause ?? 0,
        termSeasons: off.termSeasons ?? 3,
      };
      const pInstId = off.playerInstanceId || p.playerInstanceId || off.id || '';
      const offerId = off.id || `off-${pInstId}`;
      const displayName = p.displayName || off.displayName || off.playerName || 'Futbolista';
      const pos = this.normalizePos(p.position || off.position || 'DEL');
      const marketVal = Number(p.marketValue ?? terms.transferAmount ?? 5000000);

      items.push({
        id: offerId,
        listingId: off.listingId || '',
        playerInstanceId: pInstId,
        playerDefinitionId: p.playerDefinitionId || off.playerDefinitionId,
        isAmateur: p.type === 'AMATEUR' || p.isAmateur === true,
        displayName,
        position: pos,
        marketValue: marketVal,
        transferAmount: Number(terms.transferAmount || 0),
        annualSalary: Number(terms.annualSalary || 0),
        releaseClause: Number(terms.releaseClause || 0),
        termSeasons: Number(terms.termSeasons || 1),
        status: off.status || 'SUBMITTED',
        submittedAt: off.submittedAt,
        cutoffAt: off.cutoffAt,
        reservationExpiresAt: off.reservationExpiresAt,
        reservationId: off.reservationId,
        version: off.version,
        hasUserOffer: true,
        estimatedPlayerDecision: off.estimatedPlayerDecision || p.estimatedPlayerDecision,
      });

      seenOfferIds.add(offerId);
      if (pInstId) seenPlayerInstanceIds.add(pInstId);
    }

    // Incluir cualquier oferta local que no esté ya en los datos del servidor
    for (const off of localOffers) {
      if (off.fromClubId && off.fromClubId !== this.game.userClub()?.id) continue;
      if (['WON', 'LOST', 'CANCELLED', 'FAILED', 'COMPLETED', 'REJECTED', 'EXPIRED'].includes(off.status)) continue;
      const pId = off.playerInstanceId || off.playerId;
      if (!seenOfferIds.has(off.id) && !seenPlayerInstanceIds.has(pId)) {
        const player = this.game.allPlayers().find(p => (p.playerInstanceId || p.id) === pId);
        items.push({
          id: off.id,
          listingId: off.listingId || '',
          playerInstanceId: pId,
          playerDefinitionId: player?.playerDefinitionId,
          isAmateur: player?.isAmateur || player?.playerType === 'AMATEUR',
          displayName: off.playerName || 'Futbolista',
          position: this.normalizePos(off.position || 'DEL'),
          marketValue: off.transferFeeM * 1_000_000,
          transferAmount: off.transferFeeM * 1_000_000,
          annualSalary: off.offeredSalaryM * 1_000_000,
          releaseClause: off.offeredReleaseClauseM * 1_000_000,
          termSeasons: off.contractDurationYears,
          status: off.status || 'SUBMITTED',
          submittedAt: off.createdOn,
          cutoffAt: off.cutoffAt,
          reservationId: off.reservationId,
          hasUserOffer: true,
          estimatedPlayerDecision: off.estimatedPlayerDecision,
        });
        seenOfferIds.add(off.id);
        if (pId) seenPlayerInstanceIds.add(pId);
      }
    }

    const buyerClubId = this.game.userClub()?.id;
    for (const offer of this.clubTransfers()) {
      if (offer.buyerClubId !== buyerClubId || seenOfferIds.has(offer.id)) continue;
      const player = this.clubListedPlayers().find(p => (p.playerInstanceId || p.id) === offer.playerInstanceId)
        || this.game.allPlayers().find(p => (p.playerInstanceId || p.id) === offer.playerInstanceId);
      const definitionId = offer.player?.playerDefinitionId || player?.playerDefinitionId;
      const profile = definitionId ? this.clubTransferProfiles()[definitionId] : undefined;
      items.push({
        id: offer.id, source: 'CLUB', operationType: offer.type, listingId: offer.listingId || '', playerInstanceId: offer.playerInstanceId,
        playerDefinitionId: definitionId,
        isAmateur: player?.isAmateur || player?.playerType === 'AMATEUR',
        displayName: profile?.displayName || player?.name || `Jugador ${offer.playerInstanceId.slice(0, 8)}`,
        position: profile ? this.normalizePos(profile.position) : player?.position || '—',
        marketValue: profile?.marketValue ?? (player ? player.currentMarketValue * 1_000_000 : 0),
        marketValueKnown: profile?.marketValue != null || !!player,
        transferAmount: offer.terms.transferAmount, annualSalary: offer.terms.annualSalary,
        releaseClause: offer.terms.releaseClause, termSeasons: offer.terms.termSeasons,
        status: offer.status, clubDecision: offer.clubDecision, playerDecision: offer.playerDecision,
        submittedAt: offer.submittedAt, cutoffAt: offer.decisionDeadline || offer.executionCutoff,
        reservationId: offer.reservationId, version: offer.version, hasUserOffer: true,
        estimatedPlayerDecision: (offer as any).estimatedPlayerDecision,
      });
      seenOfferIds.add(offer.id);
    }
    return items;
  }

  getCombinedOffers(): ActiveOfferItem[] {
    const items = this.rawOffersList();
    if (this.offersFilter === 'submitted') {
      return items.filter((i) => i.hasUserOffer);
    }
    return items;
  }

  isOfferListLoading(): boolean {
    return (this.offerListFilter !== 'history' && (this.game.isActiveOffersLoading() || this.clubOffersLoading())) ||
      (this.offerListFilter !== 'active' && this.game.isHistoricalOffersLoading());
  }

  unifiedOffers(): Array<{ key: string; active: ActiveOfferItem | null; history: BackendHistoricalOfferItem | null; submittedAt?: string }> {
    const history = this.historicalOffersList();
    const historicalIds = new Set(history.map(item => item.id));
    const rows: ReturnType<MarketComponent['unifiedOffers']> = [];
    if (this.offerListFilter !== 'history') {
      for (const offer of this.rawOffersList()) {
        if (offer.hasUserOffer && !historicalIds.has(offer.id)) {
          rows.push({ key: `active:${offer.id}`, active: offer, history: null, submittedAt: offer.submittedAt });
        }
      }
    }
    if (this.offerListFilter !== 'active') {
      for (const item of this.filteredHistoricalOffers()) {
        rows.push({ key: `history:${item.id}`, active: null, history: item, submittedAt: item.submittedAt });
      }
    }
    const timestamp = (value?: string) => Date.parse(value || '') || 0;
    return rows.sort((a, b) => timestamp(b.submittedAt) - timestamp(a.submittedAt) || a.key.localeCompare(b.key));
  }

  groupedOffers(): Array<{ key: string; label: string; items: ReturnType<MarketComponent['unifiedOffers']> }> {
    const rows = this.unifiedOffers();
    return this.groupOfferDates(rows.filter(row => row.active), rows.filter(row => row.history), row => row.submittedAt);
  }

  mySalesOfferSections(group: MySalesPlayerGroup) {
    const now = new Date(this.currentTime());
    const todayKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const dates = this.mySalesOfferDates(group);
    const otherDays = dates.filter(day => day.key !== todayKey);
    return {
      today: dates.filter(day => day.key === todayKey),
      otherDays,
      otherOffersCount: otherDays.reduce((count, day) => count + day.items.length, 0),
    };
  }

  mySalesOfferDates(group: MySalesPlayerGroup) {
    const offers = group.rounds.flatMap(round => round.offers);
    const dates = this.groupOfferDates([], offers, offer => offer.presentedAt || offer.submittedAt);
    return dates.map(day => ({
      ...day,
      items: [...day.items].sort((a, b) => (b.terms?.transferAmount || 0) - (a.terms?.transferAmount || 0)),
      failedRoundIds: [...new Set(day.items.map(offer => offer.roundId ? String(offer.roundId) : 'default'))]
        .filter(id => this.syncFailedRounds().has(id)),
    }));
  }

  private groupOfferDates<T>(active: T[], history: T[], submittedAt: (item: T) => string | undefined): Array<{ key: string; label: string; items: T[] }> {
    const groups: Array<{ key: string; label: string; items: T[] }> = [];
    if (active.length) groups.push({ key: 'active', label: 'Activas', items: active });
    const dayKey = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const days = new Map<string, { key: string; label: string; items: T[] }>();
    for (const row of history) {
      const date = new Date(submittedAt(row) || '');
      const valid = !Number.isNaN(date.getTime());
      const key = valid ? dayKey(date) : 'undated';
      let group = days.get(key);
      if (!group) {
        const formatted = valid ? date.toLocaleDateString('es-ES', {
          day: 'numeric', month: 'long', year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
        }) : 'Sin fecha';
        const label = key === dayKey(now) ? `Hoy · ${formatted}` : key === dayKey(yesterday) ? `Ayer · ${formatted}` : formatted;
        group = { key, label, items: [] };
        days.set(key, group);
      }
      group.items.push(row);
    }
    groups.push(...[...days.values()].sort((a, b) => a.key === 'undated' ? 1 : b.key === 'undated' ? -1 : b.key.localeCompare(a.key)));
    return groups;
  }

  totalCommittedFundsM(): number {
    return this.rawOffersList()
      .filter((o) => o.source === 'CLUB' || o.status === 'SUBMITTED' || o.status === 'PENDING')
      .reduce((acc, curr) => acc + ((curr.transferAmount + curr.annualSalary) / 1_000_000), 0);
  }

  latestCutoffAt(): string {
    const list = this.rawOffersList();
    const withCutoff = list.find((o) => !!o.cutoffAt);
    return withCutoff?.cutoffAt || '2026-09-29T01:00:00Z';
  }

  listingIdLabel(): string {
    const list = this.rawOffersList();
    const withListing = list.find((o) => !!o.listingId);
    return withListing?.listingId || '5f7df771-b3a1-4c19-98df-18add4e0690c';
  }

  openNegotiationForOfferItem(item: ActiveOfferItem): void {
    if (!this.canEditOffer(item)) return;
    const clubOffer = item.source === 'CLUB' ? this.clubTransfers().find(o => o.id === item.id) : undefined;
    // Buscar si ya existe el jugador en las listas completas
    let player = this.game
      .allPlayers()
      .find((p) => p.playerInstanceId === item.playerInstanceId || p.id === item.playerInstanceId);

    if (!player) {
      // Construir objeto Player virtual con los datos oficiales
      const pos = (['POR', 'DEF', 'MED', 'DEL'].includes(item.position) ? item.position : 'DEL') as Position;
      player = {
        id: item.playerInstanceId,
        playerInstanceId: item.playerInstanceId,
        listingId: item.listingId || this.listingIdLabel(),
        name: item.displayName,
        shortName: item.displayName,
        age: 25,
        nationality: 'España',
        position: pos,
        overall: 78,
        potential: 83,
        stats: { pace: 77, shooting: 74, passing: 76, dribbling: 77, defending: 65, physical: 73 },
        condition: 100,
        morale: 'EXCELLENT',
        isAmateur: false,
        listedForTransfer: false,
        formScore: 4,
        contract: {
          salary: item.annualSalary / 1_000_000,
          durationYears: item.termSeasons,
          releaseClause: item.releaseClause / 1_000_000,
          acquisitionCost: item.transferAmount / 1_000_000,
          startSeason: 1,
          endSeason: 1 + item.termSeasons,
        },
        clubId: null,
        currentMarketValue: item.marketValue / 1_000_000,
        attributes: { aggression: 70, composure: 75, stamina: 80, positioning: 76 },
      };
    }

    if (clubOffer) player = { ...player, clubId: clubOffer.sellerClubId, listingId: clubOffer.listingId || '', marketSource: 'CLUB_LISTING' };
    this.editingOfferId = item.id;
    this.editingOfferEstimatedDecision.set(item.estimatedPlayerDecision || null);
    this.openNegotiationModal(player, item.transferAmount / 1_000_000, true);
    this.editingOfferId = item.id;
    this.editingOfferEstimatedDecision.set(item.estimatedPlayerDecision || null);
    this.editingClubOffer = clubOffer || null;
    if (item.hasUserOffer) {
      this.offeredTransferFee = item.transferAmount / 1_000_000;
      this.offeredSalary = item.annualSalary / 1_000_000;
      this.contractYears = item.termSeasons;
      this.offeredClause = item.releaseClause / 1_000_000;
    }
  }

  canEditOffer(item: ActiveOfferItem): boolean {
    if (item.source !== 'CLUB') return true;
    return this.clubTransfers().some(o => o.id === item.id && o.type === 'NEGOTIATED' && o.status === 'SUBMITTED');
  }

  canCancelClubOffer(item: ActiveOfferItem): boolean {
    return item.source === 'CLUB' && this.clubTransfers().some(o => o.id === item.id && o.type === 'NEGOTIATED' &&
      ['PREPARING', 'SUBMITTED', 'AWAITING_SELLER'].includes(o.status));
  }

  private normalizePos(raw: string): string {
    const p = (raw || '').toUpperCase();
    if (p.includes('FORWARD') || p.includes('DEL') || p.includes('ATTACK')) return 'DEL';
    if (p.includes('MIDFIELD') || p.includes('MED')) return 'MED';
    if (p.includes('DEFEND') || p.includes('DEF')) return 'DEF';
    if (p.includes('GOAL') || p.includes('POR') || p.includes('GK')) return 'POR';
    return 'DEL';
  }

  getClubName(clubId: string): string {
    if (this.sellerClubNames()[clubId]) return this.sellerClubNames()[clubId];
    const found = this.game.clubs().find((c) => c.id === clubId);
    return found ? found.name : 'Nombre no disponible';
  }

  getPlayerClubShortName(player: Player): string | null {
    if (player.realClub?.shortName) {
      return player.realClub.shortName;
    }
    if (player.clubShortName) {
      return player.clubShortName;
    }
    if (player.clubId) {
      const found = this.game.clubs().find((c) => c.id === player.clubId);
      return found?.code || null;
    }
    return null;
  }

  getPlayerClubName(player: Player): string {
    if (player.realClub?.name) {
      return player.realClub.name;
    }
    if (player.clubName) {
      return player.clubName;
    }
    if (player.clubId) {
      return this.getClubName(player.clubId);
    }
    return 'Agente Libre';
  }

  getCoachCrestUrl(coach: CoachListing): string | null {
    return coach.realClub?.shortName ? 'crest/' + coach.realClub.shortName.toUpperCase() + '.png' : null;
  }

  getPlayerCrestUrl(player: Player): string | null {
    const shortName = this.getPlayerClubShortName(player);
    if (!shortName) return null;
    return `crest/${shortName.toUpperCase()}.png`;
  }

  onCrestError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img && img.src) {
      if (!img.dataset['triedFallback']) {
        img.dataset['triedFallback'] = 'true';
        if (img.src.includes('/assets/crest/')) {
          img.src = img.src.replace('/assets/crest/', '/crest/');
          return;
        } else if (img.src.includes('/crest/')) {
          img.src = img.src.replace('/crest/', '/assets/crest/');
          return;
        }
      }
      img.style.display = 'none';
    }
  }

  getPositionBadge(pos: Position | string): string {
    switch (pos) {
      case 'POR':
        return 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/20 dark:text-amber-300 dark:border-amber-500/40';
      case 'DEF':
        return 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/20 dark:text-blue-300 dark:border-blue-500/40';
      case 'MED':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/20 dark:text-emerald-300 dark:border-emerald-500/40';
      case 'DEL':
        return 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/20 dark:text-rose-300 dark:border-rose-500/40';
      default:
        return 'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700';
    }
  }

  toggleTransferListing(player: Player): void {
    if (player.listedForTransfer) {
      this.game.removePlayerFromSale(player.id);
    } else {
      const price = Number((player.currentMarketValue || player.contract?.releaseClause || 5.0).toFixed(1));
      this.game.listPlayerForSale(player.id, price);
    }
  }

  getOfferStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      SUBMITTED: 'Oferta enviada', ENVIADA: 'Oferta enviada',
      PENDING: 'Oferta pendiente', PENDIENTE: 'Oferta pendiente',
      CONTRACT_PENDING: 'En valoración',
      PREPARING: 'Preparando oferta', UPDATING: 'Actualizando oferta',
      CANCEL_PENDING: 'Cancelación pendiente', CANCELLED: 'Oferta cancelada',
      FAILURE_PENDING: 'Finalizando oferta', FAILED: 'Oferta fallida',
      RESOLVING: 'Resolviendo oferta', LOSS_PENDING: 'Resolviendo oferta',
      WON: 'Oferta ganada', LOST: 'Oferta superada',
      ACCEPTED: 'Oferta aceptada', ACEPTADA: 'Oferta aceptada',
      REJECTED: 'Oferta rechazada', RECHAZADA: 'Oferta rechazada',
      AWAITING_SELLER: 'Pendiente del vendedor', AWAITING_BUYER: 'Pendiente del comprador',
      AWAITING_PLAYER: 'Pendiente del jugador', PROCESSING: 'En tramitación',
      RELEASING: 'Liberando reserva', COMPLETED: 'Fichaje completado', EXPIRED: 'Oferta caducada',
    };
    return labels[(status || '').toUpperCase()] || 'Oferta en trámite';
  }

  getStatusBadge(status: string): string {
    switch (status.toUpperCase()) {
      case 'SUBMITTED':
      case 'ENVIADA':
        return 'bg-emerald-50 text-emerald-700 border-emerald-300 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-700';
      case 'CONTRACT_PENDING':
      case 'PENDING':
      case 'PENDIENTE':
        return 'bg-amber-50 text-amber-700 border-amber-300 dark:bg-amber-950/60 dark:text-amber-300 dark:border-amber-700';
      case 'ACCEPTED':
      case 'ACEPTADA':
        return 'bg-blue-50 text-blue-700 border-blue-300 dark:bg-blue-950/60 dark:text-blue-300 dark:border-blue-700';
      case 'REJECTED':
      case 'RECHAZADA':
        return 'bg-rose-50 text-rose-700 border-rose-300 dark:bg-rose-950/60 dark:text-rose-300 dark:border-rose-700';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700';
    }
  }

  getUnavailabilityBadgeClass(type?: string): string {
    const t = (type || '').toUpperCase();
    if (t === 'INJURY' || t === 'LESIÓN' || t === 'LESION') {
      return 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/60 dark:text-rose-300 dark:border-rose-900/60';
    }
    if (t === 'SANCTION' || t === 'SANCION' || t === 'SUSPENSION' || t === 'CARD') {
      return 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/60 dark:text-amber-300 dark:border-amber-900/60';
    }
    return 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700';
  }

  getUnavailabilityIcon(type?: string): string {
    const t = (type || '').toUpperCase();
    if (t === 'INJURY' || t === 'LESIÓN' || t === 'LESION') {
      return 'activity';
    }
    if (t === 'SANCTION' || t === 'SANCION' || t === 'SUSPENSION' || t === 'CARD') {
      return 'alert-circle';
    }
    return 'alert-circle';
  }

  getUnavailabilityTypeLabel(type?: string | null): string {
    return getUnavailabilityTypeLabel(type);
  }

  getUnavailabilityReasonLabel(reason?: string | null): string {
    return getUnavailabilityReasonLabel(reason);
  }

  pendingPlayerOffer(player: Player): ActiveOfferItem | undefined {
    const playerId = player.playerInstanceId || player.id;
    const historicalIds = new Set(this.historicalOffersList().map(offer => offer.id));
    return this.rawOffersList().find(offer => offer.playerInstanceId === playerId &&
      !historicalIds.has(offer.id) &&
      !['CANCELLED', 'FAILED', 'LOST', 'WON', 'COMPLETED', 'REJECTED', 'EXPIRED'].includes(offer.status));
  }

  openNegotiationModal(player: Player, defaultFee = 0, editing = false) {
    if (player.clubId === this.game.userClub().id || this.isSubmittingOffer()) return;
    if (!editing && this.pendingPlayerOffer(player)) return;
    if (!editing) {
      this.editingOfferId = null;
      this.editingOfferEstimatedDecision.set(null);
    }
    this.editingClubOffer = null;
    this.negotiatingPlayer = player;
    this.modalError.set(null);
    const marketValM = player.currentMarketValue || 6.0;
    this.offeredTransferFee = defaultFee > 0 ? defaultFee : player.marketSource === 'CLUB_LISTING'
      ? (player.askingPrice ?? marketValM) : Number(marketValM.toFixed(1));
    this.offeredSalary =
      player.contract?.salary && player.contract.salary >= 1.0
        ? Number(player.contract.salary.toFixed(2))
        : 1.0;
    this.contractYears = 1;
    this.offeredClause = player.marketSource === 'CLUB_LISTING' ? Number(
      (player.contract?.releaseClause && player.contract.releaseClause >= 20.0
        ? player.contract.releaseClause
        : Math.max(20.0, marketValM * 3)
      ).toFixed(1)
    ) : 10.0;
    this.offeredSigningBonus = Number(Math.max(0.3, marketValM * 0.08).toFixed(2));
  }

  closeNegotiationModal(): void {
    if (this.isSubmittingOffer() || this.isCancellingOffer()) return;
    this.negotiatingPlayer = null;
    this.editingOfferId = null;
    this.editingClubOffer = null;
    this.editingOfferEstimatedDecision.set(null);
    this.modalError.set(null);
    this.isCancellingOffer.set(false);
  }

  /**
   * Cancela la oferta formal llamando a PUT /api/market/offers/{offerId}/cancellation
   */
  async cancelTransferOffer(item?: ActiveOfferItem): Promise<void> {
    const id = item?.id || this.editingOfferId;
    if (!id || this.isCancellingOffer() || this.isSubmittingOffer()) return;
    const clubOffer = item?.source === 'CLUB' ? this.clubTransfers().find(o => o.id === id) : this.editingClubOffer;
    if (item?.source === 'CLUB' && (!clubOffer || !this.canCancelClubOffer(item))) return;
    this.isCancellingOffer.set(true);
    this.modalError.set(null);
    this.clubOffersError.set(null);

    try {
      if (clubOffer) {
        const result = await marketApi.cancelClubOffer(id, clubOffer.version);
        this.game.showNotification(result.status === 'CANCELLED' ? 'Oferta cancelada.' : 'Cancelación registrada. Pendiente de liberar la reserva.', 'success');
      } else {
        await this.game.cancelOffer(id);
      }
      this.isCancellingOffer.set(false);
      if (id === this.editingOfferId) this.closeNegotiationModal();
      await this.loadActiveOffers();
    } catch (err: any) {
      const msg = err?.message || 'Error al cancelar la oferta.';
      this.modalError.set(msg);
      if (clubOffer) this.clubOffersError.set(msg);
    } finally {
      this.isCancellingOffer.set(false);
    }
  }

  evaluationPreview() {
    if (!this.negotiatingPlayer || this.negotiatingPlayer.marketSource === 'CLUB_LISTING') return null;
    return evaluatePlayerContractAcceptance(
      this.negotiatingPlayer,
      this.offeredSalary,
      this.contractYears,
      this.offeredClause,
      this.game.userClub().division,
      this.offeredSigningBonus
    );
  }

  totalRequiredFunds(): number {
    if (!this.negotiatingPlayer) return 0;
    return Number((this.offeredTransferFee + this.offeredSalary).toFixed(2));
  }

  hasSufficientFunds(): boolean {
    const bal = this.game.userClub()?.economy?.availableBalance;
    if (bal === undefined || bal === null || bal <= 0) return true;
    return bal >= this.totalRequiredFunds();
  }

  handleInstantBuyout(player: Player) {
    if (this.pendingPlayerOffer(player) || this.isSubmittingOffer()) return;
    const res = this.game.buyoutReleaseClause(player.id);
    if (!res.success) {
      this.game.showNotification(res.message, 'error');
    }
  }

  async submitTransferOffer(): Promise<void> {
    if (!this.negotiatingPlayer || this.isSubmittingOffer()) return;
    const player = this.negotiatingPlayer;
    if (!this.editingOfferId && this.pendingPlayerOffer(player)) return;
    if (player.marketSource === 'CLUB_LISTING') {
      await this.submitClubTransferOffer(player);
      return;
    }

    const listingId =
      player.listingId ||
      this.game.marketListingInfo()?.id ||
      this.listingIdLabel() ||
      (typeof clubStorage !== 'undefined' ? clubStorage.getItem('fm_real_market_listing_id') : null) ||
      '5f7df771-b3a1-4c19-98df-18add4e0690c';

    const playerInstanceId = player.playerInstanceId || player.id;

    const payload = {
      transferAmount: Math.round((Number(this.offeredTransferFee) || 0) * 1_000_000),
      annualSalary: Math.round((Number(this.offeredSalary) || 0) * 1_000_000),
      releaseClause: Math.round((Number(this.offeredClause) || 0) * 1_000_000),
      termSeasons: 1,
    };

    this.isSubmittingOffer.set(true);
    this.modalError.set(null);

    try {
      if (this.editingOfferId) {
        // ACTUALIZACIÓN DE OFERTA EXISTENTE -> LLAMADA PUT /api/market/offers/{offerId}
        await this.game.updatePlayerOffer(this.editingOfferId, payload, listingId, playerInstanceId, player);
      } else {
        // CREACIÓN DE NUEVA OFERTA -> LLAMADA POST A LA API
        await this.game.submitPlayerOffer(listingId, playerInstanceId, payload, player);
      }
      await this.loadActiveOffers();
      // Éxito: cerrar el formulario de negociación
      this.isSubmittingOffer.set(false);
      this.closeNegotiationModal();
    } catch (err: any) {
      // Error (ej. HTTP 409 INSUFFICIENT_FUNDS o genérico):
      // Mantener abierto el formulario y conservar los valores introducidos
      const msg = err?.translatedMessage || err?.message || 'Error al procesar la oferta.';
      this.modalError.set(msg);
    } finally {
      this.isSubmittingOffer.set(false);
    }
  }

  private async submitClubTransferOffer(player: Player): Promise<void> {
    this.modalError.set(null);
    const buyerClubId = this.game.userClub()?.id;
    if (!player.listingId || !buyerClubId || player.clubId === buyerClubId) {
      this.modalError.set('No se puede presentar una oferta por este anuncio.');
      return;
    }
    const terms = {
      playerInstanceId: player.playerInstanceId || player.id, buyerClubId,
      transferAmount: Math.round(Number(this.offeredTransferFee) * 1_000_000 * 100) / 100,
      annualSalary: Math.round(Number(this.offeredSalary) * 1_000_000 * 100) / 100,
      releaseClause: Math.round(Number(this.offeredClause) * 1_000_000 * 100) / 100,
      termSeasons: this.editingClubOffer ? this.contractYears : 1,
    };
    if ([terms.transferAmount, terms.annualSalary, terms.releaseClause].some(value => !Number.isFinite(value) || value < 0.01)) {
      this.modalError.set('El importe, el sueldo y la cláusula deben ser mayores que cero.');
      return;
    }
    const editing = this.editingClubOffer;
    const key = JSON.stringify([player.listingId, editing?.id, editing?.version, terms]);
    let operationId = this.clubOfferOperationIds.get(key);
    if (!operationId) {
      operationId = crypto.randomUUID();
      this.clubOfferOperationIds.set(key, operationId);
    }
    this.isSubmittingOffer.set(true);
    try {
      if (editing) {
        await marketApi.updateClubOffer(editing.id, { updateId: operationId, expectedVersion: editing.version,
          transferAmount: terms.transferAmount, annualSalary: terms.annualSalary,
          releaseClause: terms.releaseClause, termSeasons: this.contractYears });
        this.game.showNotification('Oferta actualizada.', 'success');
        this.isSubmittingOffer.set(false);
        this.closeNegotiationModal();
        await this.loadActiveOffers();
        return;
      }
      const result = await marketApi.submitClubOffer(player.listingId, { operationId, ...terms });
      if (['FAILED', 'REJECTED', 'CANCELLED', 'EXPIRED'].includes(result.status) || result.releaseOutcome) {
        throw Object.assign(new Error(result.failure?.message || 'La oferta no ha sido aceptada por el mercado.'),
          { failure: result.failure, responseBody: result });
      }
      this.game.showNotification(result.status === 'PREPARING'
        ? 'Oferta registrada, pendiente de confirmar la reserva de fondos.'
        : 'Oferta presentada. El propietario la recibirá en el cierre del mercado.', 'success');
      this.isSubmittingOffer.set(false);
      this.closeNegotiationModal();
      await this.loadActiveOffers();
    } catch (error: any) {
      const failure = error?.failure || error?.responseBody?.failure;
      this.modalError.set(failure?.code === 'INSUFFICIENT_FUNDS' ? getMarketOfferErrorMessage(error)
        : error?.translatedMessage || error?.message || 'No se pudo presentar la oferta. Puedes reintentarlo.');
      // Only a confirmed terminal operation permits a new attempt. Network errors
      // and pending cleanup must keep their idempotency key.
      if (!editing && error?.responseBody?.id === operationId && error?.responseBody?.status === 'FAILED') {
        this.clubOfferOperationIds.delete(key);
      }
      if (editing) {
        await this.loadActiveOffers();
        const latest = this.clubTransfers().find(offer => offer.id === editing.id);
        if (latest?.status === 'SUBMITTED' && latest.updateId === operationId && latest.updateAccepted === false) {
          this.editingClubOffer = latest;
        }
      }
    } finally {
      this.isSubmittingOffer.set(false);
    }
  }

  // ==========================================
  // METODOS DEL MODULO DE ENTRENADORES (COACHES)
  // ==========================================

  async loadCoachesData(force: boolean = false): Promise<void> {
    const clubId = this.game.userClub()?.id;
    if (!clubId) return;
    const version = this.coachContextVersion;
    try {
      const groupId =
        this.activeCompetitionGroupId() ||
        (await this.competitionService.getSelectedClubId());
      if (!groupId || this.destroyed || version !== this.coachContextVersion || this.game.userClub()?.id !== clubId) return;
      this.activeCompetitionGroupId.set(groupId);
      this.lastCoachRefresh = Date.now();
      await this.game.coaches.loadAll(clubId, groupId, force);
      if (version !== this.coachContextVersion || this.destroyed) return;
      this.updateDailyCountdown();
    } catch (err) {
      console.warn('Error loading coaches data:', err);
    }
  }

  startDailyCountdownTimer(): void {
    this.stopDailyCountdownTimer();
    this.updateDailyCountdown();
    this.dailyTimerRef = setInterval(() => {
      this.updateDailyCountdown();
      if (this.activeTab === 'coaches' && Date.now() - this.lastCoachRefresh > 30_000 &&
          !this.game.coaches.isLoadingOffers() && !this.game.coaches.isLoadingMarket()) {
        void this.loadCoachesData(true);
      }
    }, 1000);
  }

  stopDailyCountdownTimer(): void {
    if (this.dailyTimerRef) {
      clearInterval(this.dailyTimerRef);
      this.dailyTimerRef = null;
    }
  }

  resolveDailyClosesAt(): string | null {
    if (this.activeTab === 'coaches') return this.game.coaches.market()?.closes_at || null;
    const info = this.game.marketListingInfo();
    if (info?.closesAt) return info.closesAt;
    if (info?.closes_at) return info.closes_at;
    if (info?.marketDate) {
      // Listings remain open until the next midnight in Madrid, including DST.
      const midnightUtc = new Date(`${info.marketDate}T00:00:00Z`);
      if (Number.isNaN(midnightUtc.getTime())) return null;
      midnightUtc.setUTCDate(midnightUtc.getUTCDate() + 1);
      const madridHour = Number(new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/Madrid', hour: '2-digit', hourCycle: 'h23',
      }).format(midnightUtc));
      return new Date(midnightUtc.getTime() - madridHour * 3_600_000).toISOString();
    }
    return null;
  }

  updateDailyCountdown(): void {
    const closesAt = this.resolveDailyClosesAt();
    if (!closesAt) {
      this.dailyTimeLeft.set('--:--:--');
      this.dailyClosesAtFormatted.set('');
      this.isDailyMarketClosed.set(true);
      return;
    }

    const closesAtTime = new Date(closesAt).getTime();
    if (isNaN(closesAtTime)) {
      this.dailyTimeLeft.set('--:--:--');
      this.dailyClosesAtFormatted.set('');
      this.isDailyMarketClosed.set(true);
      return;
    }

    try {
      const d = new Date(closesAtTime);
      this.dailyClosesAtFormatted.set(
        d.toLocaleString('es-ES', {
          timeZone: 'Europe/Madrid',
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        })
      );
    } catch {
      this.dailyClosesAtFormatted.set(closesAt);
    }

    const diffMs = closesAtTime - Date.now();
    if (diffMs <= 0) {
      const wasOpen = !this.isDailyMarketClosed();
      this.isDailyMarketClosed.set(true);
      this.dailyTimeLeft.set('00:00:00 (Cerrado)');
      if (wasOpen && Date.now() - this.lastPlayerRefresh > 30_000) {
        this.lastPlayerRefresh = Date.now();
        void this.loadFreeAgents(true);
        void this.refreshPlayerOffers(true);
      }
    } else {
      this.isDailyMarketClosed.set(false);
      const totalSecs = Math.floor(diffMs / 1000);
      const h = Math.floor(totalSecs / 3600);
      const m = Math.floor((totalSecs % 3600) / 60);
      const s = totalSecs % 60;
      const pad = (n: number) => String(n).padStart(2, '0');
      this.dailyTimeLeft.set(`${pad(h)}h ${pad(m)}m ${pad(s)}s`);
    }
  }

  coachOfferMinBonus(): number {
    return this.game.coaches.market()?.minimum_bonus ?? 1_000_000;
  }

  private isCoachContextCurrent(): boolean {
    const clubId = this.game.userClub()?.id;
    const groupId = this.activeCompetitionGroupId();
    const context = this.competitionService.activeContext();
    return !this.destroyed && !!clubId && !!groupId && context.clubId === clubId &&
      context.clubId === groupId && this.game.coaches.matchesContext(clubId, groupId);
  }

  private isCoachMarketOpen(): boolean {
    const market = this.game.coaches.market();
    return !!market?.id && market.status?.toUpperCase() === 'OPEN' &&
      new Date(market.closes_at || '').getTime() > Date.now();
  }

  canPlaceCoachOffer(): boolean {
    return this.isCoachContextCurrent() && this.isCoachMarketOpen() && this.game.coaches.canSubmitNewOffer();
  }

  canCancelCoachOffer(offer: CoachOffer | null): boolean {
    const state = this.game.coaches;
    return !!offer && this.isCoachContextCurrent() && this.isCoachMarketOpen() &&
      offer.market_id === state.market()?.id && state.pendingOffers().some(item => item.id === offer.id) &&
      state.offersLoaded() && !state.offersError() && !state.marketError() &&
      !state.isLoadingOffers() && !state.isLoadingMarket() && !state.isSubmittingOffer() && !state.isCancellingOffer();
  }

  coachOfferAwaitingResolution(offer: CoachOffer): boolean {
    return offer.market_id !== this.game.coaches.market()?.id || !this.isCoachMarketOpen();
  }

  async retryCoachOffer(): Promise<void> {
    const retry = this.game.coaches.retryOffer();
    const clubId = this.game.userClub()?.id;
    const groupId = this.activeCompetitionGroupId();
    if (!retry || !clubId || !groupId || !this.isCoachContextCurrent() || this.game.coaches.isSubmittingOffer()) return;
    const version = this.coachContextVersion;
    try {
      await this.game.coaches.submitOffer(clubId, groupId, retry.marketId, retry.coachId, retry.offerId, retry.dto);
      if (version === this.coachContextVersion && !this.destroyed) this.game.showNotification('Oferta de entrenador confirmada.', 'info');
    } catch (err) {
      if (version === this.coachContextVersion && !this.destroyed) this.game.showNotification(this.game.coaches.extractErrorMessage(err, 'No se pudo confirmar la oferta. Puedes reintentar.'), 'error');
    }
  }

  coachSigningBonusError(): string | null {
    if (this.coachSigningBonus === null || this.coachSigningBonus === undefined || isNaN(this.coachSigningBonus)) {
      return 'Debes introducir la prima de fichaje.';
    }
    if (this.coachSigningBonus < 0) {
      return 'La prima de fichaje no puede ser negativa.';
    }
    return null;
  }

  coachAnnualSalaryError(): string | null {
    if (this.coachAnnualSalary === null || this.coachAnnualSalary === undefined || isNaN(this.coachAnnualSalary)) {
      return 'Debes introducir el salario de temporada.';
    }
    if (this.coachAnnualSalary <= 0) {
      return 'El salario de temporada debe ser mayor que 0 €.';
    }
    return null;
  }

  coachReleaseClauseError(): string | null {
    if (this.coachReleaseClause === null || this.coachReleaseClause === undefined || isNaN(this.coachReleaseClause)) {
      return 'Debes introducir la cláusula de rescisión.';
    }
    if (this.coachReleaseClause < 0) {
      return 'La cláusula no puede ser negativa.';
    }
    return null;
  }

  isCoachOfferValid(): boolean {
    return (
      !this.coachSigningBonusError() &&
      !this.coachAnnualSalaryError() &&
      !this.coachReleaseClauseError() &&
      this.canPlaceCoachOffer()
    );
  }

  coachLiveScore(): number {
    return calculateCoachOfferScore(this.coachSigningBonus, this.coachAnnualSalary);
  }

  openCoachOfferModal(coach: CoachListing): void {
    if (!this.canPlaceCoachOffer()) return;
    this.selectedCoachForOffer.set(coach);
    this.coachModalError.set(null);
    this.coachModalSuccess.set(null);

    this.coachSigningBonus = this.coachOfferMinBonus();
    this.coachAnnualSalary = 2_000_000;
    this.coachReleaseClause = 10_000_000;

    // Generar un UUID único por intención de oferta
    this.coachOfferIdempotencyId = crypto.randomUUID();
    this.showCoachOfferModal.set(true);
  }

  closeCoachOfferModal(): void {
    this.showCoachOfferModal.set(false);
    this.selectedCoachForOffer.set(null);
    this.coachModalError.set(null);
    this.coachModalSuccess.set(null);
  }

  async submitCoachOffer(): Promise<void> {
    const version = this.coachContextVersion;
    const coach = this.selectedCoachForOffer();
    const market = this.game.coaches.market();
    const clubId = this.game.userClub()?.id;
    const groupId = this.activeCompetitionGroupId();

    if (!coach || !market?.id || !clubId || !groupId) {
      this.coachModalError.set('Faltan datos de la sesión o del mercado para formalizar la oferta.');
      return;
    }
    if (!this.isCoachOfferValid()) {
      return;
    }

    this.coachModalError.set(null);
    const dto: CoachOfferDto = {
      signingBonus: Math.round(Number(this.coachSigningBonus) * 100) / 100,
      annualSalary: Math.round(Number(this.coachAnnualSalary) * 100) / 100,
      releaseClause: Math.round(Number(this.coachReleaseClause) * 100) / 100,
    };

    try {
      await this.game.coaches.submitOffer(
        clubId,
        groupId,
        market.id,
        coach.id,
        this.coachOfferIdempotencyId,
        dto
      );
      if (version !== this.coachContextVersion || this.destroyed) return;
      this.closeCoachOfferModal();
      this.coachesSubTab = 'offers';
      this.game.showNotification('Oferta registrada. Se resolverá al cierre del mercado.', 'info');
    } catch (err: any) {
      if (version !== this.coachContextVersion || this.destroyed) return;
      const msg = this.game.coaches.extractErrorMessage(
        err,
        'No se pudo registrar la oferta formal por el entrenador.'
      );
      this.coachModalError.set(msg);
      if (this.game.coaches.retryOffer()) {
        this.closeCoachOfferModal();
        this.game.showNotification(msg, 'error');
      }
    }
  }

  promptCancelCoachOffer(offer: CoachOffer): void {
    if (!this.canCancelCoachOffer(offer)) return;
    this.cancellingCoachOffer.set(offer);
    this.showCoachCancelConfirmModal.set(true);
  }

  closeCoachCancelModal(): void {
    this.showCoachCancelConfirmModal.set(false);
    this.cancellingCoachOffer.set(null);
  }

  async confirmCancelCoachOffer(): Promise<void> {
    const version = this.coachContextVersion;
    const offer = this.cancellingCoachOffer();
    const clubId = this.game.userClub()?.id;
    const groupId = this.activeCompetitionGroupId();
    if (!offer || !clubId || !groupId || !this.canCancelCoachOffer(offer)) return;

    try {
      await this.game.coaches.cancelOffer(clubId, groupId, offer.id);
      if (version !== this.coachContextVersion || this.destroyed) return;
      this.closeCoachCancelModal();
      this.game.showNotification('Oferta de entrenador cancelada correctamente.', 'info');
    } catch (err: any) {
      if (version !== this.coachContextVersion || this.destroyed) return;
      const msg = this.game.coaches.extractErrorMessage(err, 'No se pudo cancelar la oferta.');
      this.game.showNotification(msg, 'error');
    }
  }

  toggleCoachOfferFilter(filter: 'active' | 'history'): void {
    if (this.coachOfferListFilter === 'all') this.coachOfferListFilter = filter === 'active' ? 'history' : 'active';
    else if (this.coachOfferListFilter !== filter) this.coachOfferListFilter = 'all';
    this.coachHistoryFilter = 'ALL';
  }

  setCoachHistoryFilter(status: string): void {
    this.coachOfferListFilter = 'history';
    this.coachHistoryFilter = this.coachHistoryFilter === status ? 'ALL' : status;
  }

  groupedCoachOffers() {
    const offers = [...this.game.coaches.offers()].sort((a, b) =>
      (Date.parse(b.submitted_at) || 0) - (Date.parse(a.submitted_at) || 0));
    const active = this.coachOfferListFilter === 'history' ? [] : offers.filter(o => o.status === 'PENDING');
    const history = this.coachOfferListFilter === 'active' ? [] : offers.filter(o => o.status !== 'PENDING' &&
      (this.coachHistoryFilter === 'ALL' || o.status === this.coachHistoryFilter ||
        (this.coachHistoryFilter === 'LOST' && o.status === 'INSUFFICIENT_FUNDS')));
    return this.groupOfferDates(active, history, offer => offer.submitted_at);
  }

  coachOfferName(offer: CoachOffer): string {
    return offer.name || this.game.coaches.resolveCoachName(offer.coach_id);
  }

  coachOfferImage(offer: CoachOffer): string | null {
    return offer.image_url || this.game.coaches.resolveCoachImage(offer.coach_id);
  }

  coachOfferClub(offer: CoachOffer) {
    if (offer.realClub) return offer.realClub;
    if (offer.club) return { name: offer.club, shortName: null };
    const coachId = offer.coach_id;
    return this.game.coaches.market()?.listings.find(coach => coach.id === coachId)?.realClub ??
      this.game.coaches.contracts().find(coach => coach.coach_id === coachId)?.realClub ?? null;
  }

  coachOfferCrest(offer: CoachOffer): string | null {
    const code = this.coachOfferClub(offer)?.shortName;
    return code ? `assets/crest/${code.toUpperCase()}.png` : null;
  }

  getCoachOfferStatusBadge(status?: string): { label: string; class: string; icon: string } {
    const s = (status || '').toUpperCase();
    switch (s) {
      case 'PENDING':
        return {
          label: this.game.t('coaches.status_pending'),
          class: 'bg-amber-50 text-amber-800 border-amber-300 dark:bg-amber-950/60 dark:text-amber-300 dark:border-amber-700',
          icon: 'clock',
        };
      case 'WON':
        return {
          label: this.game.t('coaches.status_won'),
          class: 'bg-emerald-50 text-emerald-800 border-emerald-300 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-700',
          icon: 'trophy',
        };
      case 'LOST':
        return {
          label: this.game.t('coaches.status_lost'),
          class: 'bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700',
          icon: 'x-circle',
        };
      case 'INSUFFICIENT_FUNDS':
        return {
          label: this.game.t('coaches.status_insufficient_funds'),
          class: 'bg-rose-50 text-rose-800 border-rose-300 dark:bg-rose-950/60 dark:text-rose-300 dark:border-rose-700',
          icon: 'alert-circle',
        };
      case 'CANCELLED':
        return {
          label: this.game.t('coaches.status_cancelled'),
          class: 'bg-slate-100 text-slate-600 border-slate-300 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700',
          icon: 'ban',
        };
      default:
        return {
          label: s,
          class: 'bg-slate-100 text-slate-700 border-slate-300',
          icon: 'info',
        };
    }
  }

  onCoachImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img) {
      img.onerror = null;
      img.src =
        'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" viewBox="0 0 24 24" fill="none" stroke="%2394a3b8" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>';
    }
  }

  coachIndemnityEstimate(): number | null {
    const contract = this.game.coaches.activeContract();
    if (!contract || !contract.active) return null;
    const totalRounds = contract.total_rounds || 38;
    const currentRound = this.game.currentMatchdayNumber() || 1;
    const remainingRounds = Math.max(0, totalRounds - currentRound);
    if (remainingRounds <= 0 || totalRounds <= 0) return 0;
    // Estimación expresamente identificada con redondeo a 2 decimales
    return Math.round((contract.annual_salary / totalRounds) * remainingRounds * 100) / 100;
  }

  onSigningBonusChange(event: Event): void {
    const val = parseFloat((event.target as HTMLInputElement).value);
    this.coachSigningBonus = isNaN(val) ? 0 : val;
  }

  onAnnualSalaryChange(event: Event): void {
    const val = parseFloat((event.target as HTMLInputElement).value);
    this.coachAnnualSalary = isNaN(val) ? 0 : val;
  }

  onReleaseClauseChange(event: Event): void {
    const val = parseFloat((event.target as HTMLInputElement).value);
    this.coachReleaseClause = isNaN(val) ? 0 : val;
  }

  // --- CARTERA Y CLAUSULAZOS ---
  openReleaseClauseModal(item: PortfolioItem): void {
    const isDiscovered = this.isPortfolioItemDiscovered(item);
    const clause = item.releaseClause ?? item.discoveredClause ?? item.player?.currentClause;
    if (!isDiscovered || clause == null || clause <= 0) {
      this.game.showNotification('La cláusula de rescisión todavía no ha sido descubierta. Se descubrirá en el próximo cierre diario.', 'info');
      return;
    }
    this.selectedPortfolioItemForClause.set(item);
    this.clauseTransferFeeM = Number((clause / 1_000_000).toFixed(2));
    this.clauseAnnualSalaryM = Math.max(1.0, Number((this.clauseTransferFeeM * 0.15).toFixed(1)));
    this.clauseContractYears = 3;
    this.clauseNewReleaseClauseM = Math.max(
      Math.round(this.clauseTransferFeeM * 1.5 * 10) / 10,
      this.clauseTransferFeeM + 5
    );
    this.clauseModalError.set(null);
    this.showReleaseClauseModal.set(true);
  }

  closeReleaseClauseModal(): void {
    this.showReleaseClauseModal.set(false);
    this.selectedPortfolioItemForClause.set(null);
    this.clauseModalError.set(null);
    this.isSubmittingClause.set(false);
  }

  async confirmReleaseClauseAttempt(): Promise<void> {
    const item = this.selectedPortfolioItemForClause();
    if (!item) return;

    if (this.clauseAnnualSalaryM <= 0) {
      this.clauseModalError.set('La ficha anual debe ser superior a 0.');
      return;
    }
    if (this.clauseContractYears < 1 || this.clauseContractYears > 5) {
      this.clauseModalError.set('La duración del contrato debe estar entre 1 y 5 años.');
      return;
    }
    if (this.clauseNewReleaseClauseM <= this.clauseTransferFeeM) {
      this.clauseModalError.set('La nueva cláusula de rescisión debe ser superior a la cláusula actual pagada.');
      return;
    }

    this.isSubmittingClause.set(true);
    this.clauseModalError.set(null);

    const clauseAmount = item.releaseClause ?? item.discoveredClause ?? (this.clauseTransferFeeM * 1_000_000);

    const payload: ReleaseClauseAttemptPayload = {
      playerId: item.playerId,
      clauseAmount,
      annualSalary: this.clauseAnnualSalaryM * 1_000_000,
      termSeasons: this.clauseContractYears,
      contractDurationYears: this.clauseContractYears,
      releaseClause: this.clauseNewReleaseClauseM * 1_000_000,
      newReleaseClause: this.clauseNewReleaseClauseM * 1_000_000,
      discoveredClause: clauseAmount,
    };

    try {
      const response = await this.game.submitReleaseClauseAttempt(payload);
      if (response && response.status === 'PENDING') {
        const pName = item.displayName || item.name || item.player?.name || 'Futbolista';
        this.game.showNotification(
          `Clausulazo presentado por ${pName}. La operación queda pendiente de resolución en el cierre diario.`,
          'success'
        );
        this.closeReleaseClauseModal();
      }
    } catch (err: any) {
      console.error('Error al presentar clausulazo:', err);
      // Criterio 11: Se muestra el error sin perder los datos introducidos
      this.clauseModalError.set(err?.message || 'No se pudo presentar el clausulazo. Revisa los fondos disponibles o las condiciones.');
    } finally {
      this.isSubmittingClause.set(false);
    }
  }

  async handleTogglePortfolio(player: Player): Promise<void> {
    const pId = player.playerInstanceId || player.id;
    if (this.game.isInWatchlist(pId)) {
      await this.removePlayerFromPortfolio(pId);
    } else {
      await this.addPlayerToPortfolio(player);
    }
  }

  async addPlayerToPortfolio(player: Player): Promise<void> {
    const myClubId = this.game.userClub()?.id;
    if (player.clubId === myClubId || this.game.userSquad().some(p => p.id === player.id || (player.playerInstanceId && p.playerInstanceId === player.playerInstanceId))) {
      this.game.showNotification('No se pueden añadir jugadores de la propia plantilla a la cartera.', 'info');
      return;
    }
    if (!player.clubId) {
      this.game.showNotification('No se pueden añadir jugadores libres a la cartera. Solo jugadores con contrato en otro club.', 'info');
      return;
    }
    if (this.game.hasMaxPortfolio()) {
      this.game.showNotification('Has alcanzado el límite máximo de 3 jugadores en cartera. Retira uno antes de añadir otro.', 'error');
      return;
    }

    try {
      await this.game.addToPortfolio(player);
      await this.loadPortfolio(false);
      this.game.showNotification(`${player.name} añadido a la cartera. Su cláusula se descubrirá al finalizar el siguiente cierre diario.`, 'info');
    } catch (err: any) {
      this.game.showNotification(err?.message || 'No se pudo añadir el jugador a la cartera.', 'error');
    }
  }

  async removePlayerFromPortfolio(playerId: string): Promise<void> {
    try {
      await this.game.removeFromPortfolio(playerId);
      this.game.showNotification('Jugador retirado de la cartera.', 'info');
    } catch (err: any) {
      this.game.showNotification(err?.message || 'No se pudo retirar de la cartera.', 'error');
    }
  }

  canAttemptClause(item: PortfolioItem): boolean {
    const isDiscovered = this.isPortfolioItemDiscovered(item);
    const clause = item.releaseClause ?? item.discoveredClause ?? item.player?.currentClause;
    return isDiscovered && clause != null && clause > 0;
  }

  isPortfolioItemDiscovered(item: PortfolioItem): boolean {
    return item.revealed === true || item.status === 'DISCOVERED' || item.discoveryStatus === 'DISCOVERED';
  }

  getPortfolioItemPosition(item: PortfolioItem): string {
    const pos = item.position || item.player?.position || 'MED';
    return this.normalizePos(String(pos));
  }

  getPortfolioItemCrestUrl(item: PortfolioItem): string | null {
    if (item.crestUrl) return item.crestUrl;
    const shortName = item.realClub?.shortName || item.clubCode;
    if (shortName) return `crest/${shortName.toUpperCase()}.png`;
    if (item.player) {
      return this.getPlayerCrestUrl(item.player as Player);
    }
    return null;
  }

  async loadPortfolio(forceRefresh = false): Promise<void> {
    const request = ++this.portfolioLoadVersion;
    const clubId = this.game.userClub()?.id;
    const current = () => !this.destroyed && request === this.portfolioLoadVersion && clubId === this.game.userClub()?.id;
    this.portfolioOwnerNames.set({});
    this.portfolioOwnerError.set(null);
    const items = await this.game.fetchPortfolio(undefined, forceRefresh);
    if (!current()) return;
    const ids = [...new Set(items.map(item => item.ownerClubId || item.sellerClubId || item.clubId)
      .filter((id): id is string => !!id))];
    if (!ids.length) return;
    try {
      const summaries = await clubsApi.getSummaries(ids);
      if (!current()) return;
      this.portfolioOwnerNames.set(Object.fromEntries(summaries.map(club => [club.id, club.name])));
      if (ids.some(id => !this.portfolioOwnerNames()[id])) {
        this.portfolioOwnerError.set('No se pudieron obtener todos los nombres de los clubes propietarios.');
      }
    } catch {
      if (current()) this.portfolioOwnerError.set('No se pudieron cargar los nombres de los clubes propietarios.');
    }
  }

  getPortfolioOwnerClubName(item: PortfolioItem): string {
    const id = item.ownerClubId || item.sellerClubId || item.clubId;
    return id ? this.portfolioOwnerNames()[id] || this.getClubName(id) : 'Nombre no disponible';
  }

  getPortfolioItemClubName(item: PortfolioItem): string {
    return item.realClub?.name || item.clubName || item.player?.clubName || (item.ownerClubId ? this.getClubName(item.ownerClubId) : 'Club rival');
  }

  getPortfolioItemMarketValueM(item: PortfolioItem): string {
    const mv = item.marketValue ?? item.currentMarketValue ?? (item.player?.marketValue ? item.player.marketValue * 1_000_000 : 0);
    return (mv / 1_000_000).toFixed(1);
  }

  getPortfolioItemClauseM(item: PortfolioItem): string | null {
    const clause = item.releaseClause ?? item.discoveredClause ?? item.player?.currentClause;
    if (clause != null && clause > 0) {
      return (clause / 1_000_000).toFixed(1);
    }
    return null;
  }
}
